import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import ReactLoading from "react-loading";
import Img from "./Img";
import import_logo from "../assets/import.svg";
import export_logo from "../assets/export.svg";

import { MelodyLogo } from "./MelogyLogo";
import { Link } from "react-router-dom";
import deleteIcon from "../assets/delete.svg";
import editIcon from "../assets/edit.svg";
import chatIcon from "../assets/chat.svg";
import searchIcone from "../assets/search.svg";
import importIcon from "../assets/import_1.svg";
import rarrow from "../assets/rarrow1.svg";
import searchDb from "../assets/search-db.png";
import okIcon from "../assets/check.svg";
import cancelIcon from "../assets/cancel.svg";
import { TextAnnotatorModal } from "./TextAnnotator";
import { buildAutoAnnotations } from "./autoAnnotate";
import { FieldEditor, fieldEditorStyles } from "./fieldEditors";
const API_BASE = "/api";

// ─────────────────────────────────────────────
// INTERACTION LIST COLUMNS (BioRECIPE-IL)
// ─────────────────────────────────────────────
const BIO_ANNOTATION_LABELS = [
  "Regulator Name",
  "Regulator Type",
  "Regulator Subtype",
  "Regulator Compartment",
  "Regulated Name",
  "Regulated Type",
  "Regulated Subtype",
  "Regulated Compartment",
  "Sign",
  "Connection Type",
  "Mechanism",
  "Site",
  "Cell Line",
  "Cell Type",
  "Tissue Type",
  "Organism",
];
const INTERACTION_COLUMNS = [
  "Statements",
  "Paper IDs",
  "Regulator Name",
  "Regulator Type",
  "Regulator Subtype",
  "Regulator HGNC Symbol",
  "Regulator Database",
  "Regulator ID",
  "Regulator Compartment",
  "Regulator Compartment ID",
  "Regulated Name",
  "Regulated Type",
  "Regulated Subtype",
  "Regulated HGNC Symbol",
  "Regulated Database",
  "Regulated ID",
  "Regulated Compartment",
  "Regulated Compartment ID",
  "Sign",
  "Connection Type",
  "Mechanism",
  "Site",
  "Cell Line",
  "Cell Type",
  "Tissue Type",
  "Organism",
  "Score",
  "Source",
];

// ─────────────────────────────────────────────
// BELL API HELPERS
// ─────────────────────────────────────────────
async function apiNormalize(reg, regulated) {
  const r = await fetch(`${API_BASE}/normalize`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ regulator: reg, regulated }),
  });
  if (!r.ok) throw new Error(`Normalize failed: ${r.status}`);
  return r.json();
}
async function apiRoute(interaction) {
  const r = await fetch(`${API_BASE}/route`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(interaction),
  });
  if (!r.ok) throw new Error(`Route failed: ${r.status}`);
  return r.json();
}
async function apiQuery(interaction, sources) {
  const r = await fetch(`${API_BASE}/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ interaction, sources }),
  });
  if (!r.ok) throw new Error(`Query failed: ${r.status}`);
  return r.json();
}
async function apiScore(evidence, interaction) {
  const r = await fetch(`${API_BASE}/score`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ interaction, evidence }),
  });
  if (!r.ok) throw new Error(`Score failed: ${r.status}`);
  return r.json();
}
async function apiExplain(interaction, evidence, scores) {
  const r = await fetch(`${API_BASE}/explain`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ interaction, evidence, scores }),
  });
  if (!r.ok) throw new Error(`Explain failed: ${r.status}`);
  return r.json();
}

// ─────────────────────────────────────────────
// FULL-TEXT RE-EXTRACTION HELPERS
// ─────────────────────────────────────────────
async function apiFullTextStatus(pmids) {
  const r = await fetch(`${API_BASE}/papers/fulltext/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pmids }),
  });
  if (!r.ok) throw new Error(`Full-text check failed: ${r.status}`);
  return r.json();
}
async function apiReextractStart(pmids, interaction, history) {
  const r = await fetch(`${API_BASE}/extract/reextract/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pmids, interaction, history }),
  });
  if (!r.ok) {
    let msg = `Re-extraction failed to start (${r.status})`;
    try {
      const j = await r.json();
      msg = j.detail || j.error || msg;
    } catch {}
    throw new Error(msg);
  }
  return r.json();
}
async function apiReextractStatus(jobId) {
  const r = await fetch(`${API_BASE}/extract/reextract/${jobId}/`);
  if (!r.ok) {
    let msg = `Re-extraction status failed (${r.status})`;
    try {
      const j = await r.json();
      msg = j.detail || j.error || msg;
    } catch {}
    throw new Error(msg);
  }
  return r.json();
}

// "PMID:38291045, PMID:123456" -> ["38291045", "123456"]
function extractPmids(paperIds) {
  return Array.from(new Set(String(paperIds || "").match(/\d{5,9}/g) || []));
}

const reextractRowKey = (r) =>
  [r["Regulator Name"], r["Regulated Name"], r["Paper IDs"]]
    .map((x) =>
      String(x || "")
        .trim()
        .toLowerCase(),
    )
    .join("|");

// Merge re-extracted rows into the table.
//  - a row with the same regulator, regulated and paper as an existing row is NOT added;
//    instead its non-empty values fill the existing row's EMPTY fields (nothing is overwritten)
//  - every other row is added at the end
function mergeReextractedRows(existing, incoming) {
  const firstIndex = new Map();
  existing.forEach((r, i) => {
    const k = reextractRowKey(r);
    if (!firstIndex.has(k)) firstIndex.set(k, i);
  });
  const KEEP = new Set(["Statements", "Paper IDs", "Source", "Score"]);
  const added = [];
  const enrich = {};
  const addedKeys = new Set();
  let duplicates = 0;
  incoming.forEach((r) => {
    const k = reextractRowKey(r);
    if (firstIndex.has(k)) {
      duplicates += 1;
      const i = firstIndex.get(k);
      const upd = {};
      INTERACTION_COLUMNS.forEach((c) => {
        if (KEEP.has(c)) return;
        if (!String(existing[i][c] || "").trim() && String(r[c] || "").trim())
          upd[c] = r[c];
      });
      if (Object.keys(upd).length) enrich[i] = { ...(enrich[i] || {}), ...upd };
    } else if (!addedKeys.has(k)) {
      addedKeys.add(k);
      added.push(r);
    }
  });
  return { added, enrich, duplicates };
}

const reextractStyles = `
.chat-reextract-bar {
  padding: 8px 20px; border-top: 1px solid #C8C9C7; background: #F4F8FF;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0;
}
.chat-reextract-btn {
  padding: 7px 14px; border: 1px solid #003594; border-radius: 6px; background: #fff;
  color: #003594; font-size: 12px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; gap: 8px; transition: all 0.2s;
}
.chat-reextract-btn:hover:not(:disabled) { background: #003594; color: #fff; }
.chat-reextract-btn:disabled { opacity: 0.65; cursor: not-allowed; }
.chat-reextract-hint { font-size: 11px; color: #555; line-height: 1.4; flex: 1; min-width: 180px; }

.info-banner {
  position: fixed; top: 20px; right: 20px; z-index: 9999; max-width: 480px;
  background-color: #E6F7EF; border: 2px solid #00AD6E; border-radius: 8px;
  padding: 16px; box-shadow: 0 4px 16px rgba(0,0,0,0.15); animation: slideIn 0.3s ease-out;
}
.info-banner-header { display: flex; justify-content: space-between; align-items: center; font-weight: bold; color: #00693e; font-size: 14px; }
.info-banner-message { color: #00205B; font-size: 13px; line-height: 1.5; margin-top: 6px; }
.info-banner-close { cursor: pointer; font-size: 20px; color: #00693e; background: none; border: none; padding: 0; line-height: 1; }
`;

// ─────────────────────────────────────────────
// BELL MOCK PIPELINE
// ─────────────────────────────────────────────
function mockBellPipeline(row) {
  const mech = (row["Mechanism"] || "").toLowerCase();
  const isPhospho = mech.includes("phospho");
  const isTF =
    mech.includes("transcription") ||
    mech.includes("activat") ||
    mech.includes("repres");
  const sign = (row["Sign"] || "").toLowerCase();
  const isPos = sign.includes("pos") || sign === "+";
  const reg = row["Regulator Name"] || "Regulator";
  const tgt = row["Regulated Name"] || "Regulated";
  const sources = isPhospho
    ? ["PhosphoSitePlus", "SIGNOR", "INDRA", "OmniPath", "Pathway Commons"]
    : isTF
      ? ["TRRUST", "INDRA", "Reactome", "OmniPath"]
      : ["INDRA", "OmniPath", "STRING"];
  const evidence = sources.map((src, i) => ({
    source: src,
    found: true,
    dir: true,
    sign: i < sources.length - 1 ? isPos : null,
    mech: isPhospho || isTF ? i < 2 : false,
    ctx: i === 0 ? "partial" : null,
    score: parseFloat((0.98 - i * 0.07).toFixed(2)),
    detail:
      src === "PhosphoSitePlus"
        ? `Ser473,T308 confirmed. Kinase: ${reg}.`
        : src === "SIGNOR"
          ? `${isPos ? "up" : "down"}-regulates via ${row["Mechanism"] || "direct"}.`
          : src === "INDRA"
            ? `Belief ${(0.88 - i * 0.05).toFixed(2)}. ${20 + i * 10} papers.`
            : src === "TRRUST"
              ? "Canonical TF-target. Manually curated."
              : `${10 + i * 5} databases corroborate.`,
  }));
  const S1 = Math.min(1, sources.length / 5),
    S2 = isPhospho || isTF ? 0.85 : 0.55,
    S3 = 0.88,
    S4 = 0;
  const conf = S1 >= 0.7 && S2 >= 0.5 ? "HIGH" : S1 >= 0.4 ? "MEDIUM" : "LOW";
  return {
    normalized: {
      regulator: `HGNC:${Math.floor(Math.random() * 9000 + 1000)}|UniProt:P${Math.floor(Math.random() * 90000 + 10000)}`,
      regulated: `HGNC:${Math.floor(Math.random() * 9000 + 1000)}|UniProt:P${Math.floor(Math.random() * 90000 + 10000)}`,
    },
    sources,
    evidence,
    scores: { S1, S2, S3, S4 },
    confidence: conf,
    flags: S2 < 0.7 ? ["cell_line_context_partial"] : [],
    contradictions: [],
    action: conf === "HIGH" ? "Accept" : "Flag for review",
    cot: {
      query:
        `Interaction: ${reg} → ${tgt} (${row["Sign"] || "?"}, ${row["Mechanism"] || "?"})\n` +
        (row["Cell Line"] ? `Cell line: ${row["Cell Line"]} · ` : "") +
        (row["Organism"] ? `Organism: ${row["Organism"]}\n` : "\n") +
        (row["Statements"]
          ? `Source statement: "${row["Statements"]}"\n\n`
          : "\n") +
        `Evidence retrieved:\n` +
        evidence
          .map(
            (ev) =>
              `  • ${ev.source}: ${ev.detail} (score ${ev.score.toFixed(2)})`,
          )
          .join("\n") +
        `\n\nScores: S1=${S1.toFixed(2)}, S2=${S2.toFixed(2)}, S3=${S3.toFixed(2)}, S4=${S4}\n\n` +
        `Task: Explain the confidence assessment and recommend an action for this interaction.`,
      answer:
        `${sources.length} databases queried for ${reg}→${tgt}.\n\n` +
        (isPhospho
          ? `PhosphoSitePlus and SIGNOR confirm phosphorylation sites. `
          : "") +
        (isTF
          ? `TRRUST and Reactome confirm transcriptional regulation. `
          : "") +
        `INDRA returns a high belief score supported by multiple independent papers.` +
        (row["Cell Line"]
          ? `\n\nCell line ${row["Cell Line"]} is context-inferred as a partial match.`
          : "") +
        ` No contradictions were detected across queried sources.`,
    },
    dataSource: "mock",
  };
}

// ─────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Mystery+Quest&display=swap');

* { margin: 0; padding: 0; box-sizing: border-box; }

body {
  font-family: Arial, sans-serif;
  background: #f4f5f7;
  height: calc(100vh - 64px);
}

.page-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
}

.body-layout {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}

.sidebar {
  width: 240px;
  min-width: 240px;
  background-color: #00205B;
  color: #DBEEFF;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  justify-content:space-between;
}

.sidebar-brand {
  padding: 40px 18px 26px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #003594;
}

.sidebar-brand-title {
  font-size: 26px;
  font-family: 'Mystery Quest', cursive;
  color: #FFFFFF;
  letter-spacing: 8px;
  line-height: 1;
}

.sidebar-section {
  padding: 14px 0;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.sidebar-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #FFFFFF;
  padding: 30px 10px 10px;
  border-bottom: 1px solid #FFFFFF;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 10px 18px;
  font-size: 13px;
  color: #DBEEFF;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  text-decoration: none;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}

.sidebar-item:hover { background: rgba(255,184,28,0.1); color: #fff; }
.sidebar-item.active { background: rgba(255,184,28,0.15); color: #FFB81C; font-weight: 600; }

.sidebar-footer {
  margin-top: auto;
  padding: 16px 18px;
  border-top: 1px solid rgba(255,255,255,0.08);
}

.sidebar-footer-text { font-size: 11px; color: #FFFFFF; line-height: 1.5; }

/* ── BELL pipeline steps in sidebar ── */
.bell-pipe-section { padding: 10px 18px 6px; }
.bell-pipe-step { display: flex; align-items: center; gap: 8px; padding: 5px 0; }
.bell-pipe-circle {
  width: 20px; height: 20px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700; border: 2px solid; flex-shrink: 0; transition: all .3s;
}
.bell-pipe-circle.done { background: #3498db; border-color: #3498db; color: #fff; }
.bell-pipe-circle.active { background: #F7911E; border-color: #F7911E; color: #fff; }
.bell-pipe-circle.pending { background: rgba(255,255,255,.1); border-color: rgba(255,255,255,.3); color: rgba(255,255,255,.4); }
.bell-pipe-label { font-size: 12px; color: #DBEEFF; }
.bell-pipe-label.done { color: #3498db; }
.bell-pipe-label.active { color: #FFB81C; }
.bell-pipe-conn { width: 2px; height: 8px; background: rgba(255,255,255,.2); margin-left: 9px; }
.bell-pipe-conn.done { background: #3498db; }

.main-area {
  flex: 1;
  padding: 32px 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
  min-width: 0;
}

.page-heading { font-size: 22px; font-weight: 700; color: #00205B; }
.page-subheading { font-size: 13px; color: #555; margin-top: 4px; line-height: 1.5; }

.step-indicator {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 0 8px;
}

.step-node {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s;
  cursor: default;
}

.step-node.completed { background: #00AD6E; color: #fff; }
.step-node.active { background: #003594; color: #fff; box-shadow: 0 2px 8px rgba(0,53,148,0.3); }
.step-node.upcoming { background: #e8eaed; color: #999; }

.step-connector { width: 40px; height: 2px; background: #C8C9C7; flex-shrink: 0; }
.step-connector.completed { background: #00AD6E; }

.step-number {
  width: 22px; height: 22px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; background: rgba(255,255,255,0.2);
}

.step-node.upcoming .step-number { background: #C8C9C7; color: #fff; }

.search-panel {
  background: #fff; border: 2px solid #C8C9C7; border-radius: 8px;
  padding: 24px; display: flex; flex-direction: column; gap: 14px;
}

.search-panel-title {
  font-size: 15px; font-weight: 700; color: #00205B;
  display: flex; align-items: center; gap: 8px;
}

.search-input-row { display: flex; gap: 10px; align-items: stretch; }

.search-input {
  flex: 1; padding: 10px 14px; font-size: 14px;
  border: 2px solid #C8C9C7; border-radius: 6px; outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input:focus { border-color: #003594; box-shadow: 0 0 0 3px rgba(0,53,148,0.1); }
.search-input::placeholder { color: #aaa; }

.search-btn {
  padding: 10px 24px; border: none; border-radius: 6px; cursor: pointer;
  font-size: 14px; font-weight: 700; background: #003594; color: #fff;
  transition: all 0.2s; display: flex; align-items: center; gap: 8px; white-space: nowrap;
}

.search-btn:hover { background: #00205B; }
.search-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.search-options { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }

.search-option-label { font-size: 12px; color: #555; display: flex; align-items: center; gap: 6px; }
.search-option-label input, .search-option-label select { font-size: 12px; padding: 4px 8px; border: 1px solid #C8C9C7; border-radius: 4px; }

.results-panel {
  background: #fff; border: 2px solid #C8C9C7; border-radius: 8px;
  display: flex; flex-direction: column; overflow: hidden;
}

.results-header {
  padding: 14px 20px; background: #fafbfc; border-bottom: 1px solid #C8C9C7;
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
}

.results-header-title { font-size: 14px; font-weight: 700; color: #00205B; }
.results-header-actions { display: flex; align-items: center; gap: 8px; }

.select-all-btn {
  padding: 5px 12px; font-size: 11px; border: 1px solid #003594; border-radius: 4px;
  background: #DBEEFF; color: #003594; cursor: pointer; font-weight: 600; transition: all 0.2s;
}

.select-all-btn:hover { background: #003594; color: #fff; }
.results-count { font-size: 12px; color: #555; padding: 0 4px; }
.results-list { max-height: 400px; overflow-y: auto; padding: 0; }

.result-item {
  display: flex; gap: 12px; padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0; transition: background 0.15s; cursor: pointer;
}

.result-item:hover { background: #DBEEFF; }
.result-item.selected { background: rgba(0,173,110,0.06); border-left: 3px solid #00AD6E; }

.result-checkbox { flex-shrink: 0; margin-top: 3px; width: 18px; height: 18px; accent-color: #003594; cursor: pointer; }
.result-content { flex: 1; min-width: 0; }
.result-title { font-size: 13px; font-weight: 600; color: #00205B; line-height: 1.4; margin-bottom: 4px; }
.result-authors { font-size: 11px; color: #777; margin-bottom: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.result-meta { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.result-pmid { font-size: 11px; font-weight: 600; color: #003594; background: #DBEEFF; padding: 2px 8px; border-radius: 3px; }
.result-journal { font-size: 11px; color: #888; font-style: italic; }
.result-date { font-size: 11px; color: #888; }

.result-abstract-toggle {
  font-size: 11px; color: #003594; cursor: pointer; border: none;
  background: none; text-decoration: underline; padding: 0; margin-top: 4px;
}

.result-abstract {
  font-size: 12px; color: #444; line-height: 1.5; margin-top: 6px;
  padding: 8px 12px; background: #fafbfc; border-radius: 4px; border: 1px solid #e8eaed;
}

.extraction-panel {
  background: #DBEEFF; border: 2px dashed #003594; border-radius: 8px;
  padding: 24px; display: flex; flex-direction: column; gap: 16px;
}

.extraction-panel-title {
  font-size: 15px; font-weight: 700; color: #00205B;
  display: flex; align-items: center; gap: 8px;
}

.extraction-options { display: flex; gap: 12px; flex-wrap: wrap; }

.extraction-method-card {
  flex: 1; min-width: 200px; padding: 18px; border-radius: 8px;
  border: 2px solid #C8C9C7; background: #fff; cursor: pointer;
  transition: all 0.2s; display: flex; flex-direction: column; gap: 6px;
}

.extraction-method-card:hover { border-color: #FFB81C; box-shadow: 0 2px 8px rgba(0,53,148,0.12); }

.extraction-method-card.selected {
  border-color: #003594; background: #fff;
  box-shadow: 0 2px 12px rgba(0,53,148,0.15);
}

.extraction-method-card.selected .method-radio { background: #003594; border-color: #003594; }

.method-header { display: flex; align-items: center; gap: 10px; }

.method-radio {
  width: 18px; height: 18px; border-radius: 50%; border: 2px solid #C8C9C7;
  background: #fff; flex-shrink: 0; transition: all 0.2s;
  display: flex; align-items: center; justify-content: center;
}

.method-radio::after { content: ''; width: 8px; height: 8px; border-radius: 50%; background: #fff; }
.method-name { font-size: 14px; font-weight: 700; color: #00205B; }
.method-icon { font-size: 20px; margin-left: auto; }
.method-description { font-size: 12px; color: #555; line-height: 1.5; padding-left: 28px; }

.method-badge {
  display: inline-block; font-size: 10px; font-weight: 700;
  padding: 2px 8px; border-radius: 3px; margin-left: 28px; width: fit-content;
}

.method-badge.indra-llm { background: #FFB81C; color: #00205B; }
.method-badge.llm { background: #00AD6E; color: #fff; }
.method-badge.indra { background: #66B2E3; color: #00205B; }

.extract-btn {
  padding: 12px 32px; border: none; border-radius: 6px; cursor: pointer;
  font-size: 15px; font-weight: 700; background: #FFB81C; color: #00205B;
  transition: all 0.25s; display: flex; align-items: center; gap: 8px; align-self: flex-start;
}

.extract-btn:hover { background: #F7911E; box-shadow: 0 2px 12px rgba(255,184,28,0.4); }
.extract-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.virtual-table-wrapper {
  display: flex; flex-direction: column; height: clamp(250px, 50vh, 65vh);
  flex-shrink: 0; overflow: hidden; border: 2px solid #C8C9C7; border-radius: 6px; background: #fff;
}

.virtual-table-toolbar {
  padding: 6px 10px; flex-shrink: 0; display: flex; align-items: center;
  gap: 10px; border-bottom: 1px solid #C8C9C7; background: #fafbfc;
}

.virtual-table-header-scroll { overflow: hidden; flex-shrink: 0; }
.virtual-table-body-scroll { flex: 1; min-height: 0; overflow: auto; }
.virtual-table-header-row { display: flex; background-color: #003594; }

.virtual-table-header-cell {
  flex-shrink: 0; padding: 8px 12px; text-align: left; font-size: 11px;
  font-weight: 600; color: #fff; letter-spacing: 0.05em;
  border-right: 1px solid rgba(255,255,255,0.15); overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; box-sizing: border-box;
  display: flex; flex-direction: column; gap: 4px;
}

.virtual-table-header-cell input {
  padding: 2px 5px; font-size: 10px; width: 100%; box-sizing: border-box;
  border-radius: 3px; border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.88); color: #000; outline: none;
}

.virtual-table-row {
  display: flex; cursor: pointer; border-bottom: 1px solid #e5e7eb; box-sizing: border-box;
}

.virtual-table-row:hover { background-color: #DBEEFF; }
.virtual-table-row:nth-child(even) { background-color: #fafbfc; }
.virtual-table-row:nth-child(even):hover { background-color: #DBEEFF; }
.virtual-table-row.editing { background-color: #DBEEFF; }
.virtual-table-row.chat-active { background-color: #FFF8E1; border-left: 3px solid #FFB81C; }
.virtual-table-row.bell-active { background-color: #eaf2fb; border-left: 3px solid #3498db; }

.virtual-table-cell {
  flex-shrink: 0; padding: 6px 12px; font-size: 12px; color: #000;
  border-right: 1px solid #C8C9C7; line-height: 1.4; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; box-sizing: border-box;
  display: flex; align-items: center;
}

.virtual-table-cell-actions { display: flex; align-items: center; justify-content: center; gap: 4px; }

.edit-input {
  width: 100%; padding: 3px 6px; font-size: 12px;
  border: 1px solid #003594; border-radius: 4px; outline: none; background: #fff;
}

.edit-input:focus { border-color: #003594; box-shadow: 0 0 0 2px rgba(0,53,148,0.15); }

.action-btn {
  padding: 4px 8px; border: none; border-radius: 4px;
  background:none;
  cursor: pointer; font-size: 11px; font-weight: 600; transition: all 0.2s; margin-right: 4px;
}

.edit-btn:hover { background: #66B2E3; color: #fff; }
.delete-btn:hover { background: #FF5B45; color: #fff; }
.chat-btn:hover { background: #FFB81C; color: #fff; }
.save-btn:hover { background: #c7feea; color: #fff; }
.cancel-btn:hover { background: #999; color: #fff; }

/* ── BELL run button in table ── */
.bell-run-btn {
  padding: 3px 8px; border-radius: 4px; cursor: pointer;
  font-size: 10px; font-weight: 700;
  border: none;
  background: none;
  transition: all 0.2s; white-space: nowrap; flex-shrink: 0;
}
.bell-run-btn:hover:not(:disabled) { background: #0e8d7e; color: #fff; }
.bell-run-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.bell-run-btn.evaluated { background: #EAF8D6; color: #1a5276; border-color: #3498db; }
.bell-run-btn.evaluated:hover:not(:disabled) { background: #0e8d7e; color: #fff; }
.bell-run-btn.running { background: #FFF8E1; color: #B87333; border-color: #FFB81C; cursor: not-allowed; }

/* ── BELL confidence mini-badge in table ── */
.bell-conf-badge {
  display: inline-block; padding: 1px 6px; border-radius: 3px;
  font-size: 9px; font-weight: 700; letter-spacing: .04em; white-space: nowrap;
}
.bell-conf-badge.high { background: #d5f5e3; color: #1e8449; border: 1px solid #82e0aa; }
.bell-conf-badge.med  { background: #FFF8E1; color: #B87333;  border: 1px solid #FFB81C; }
.bell-conf-badge.low  { background: #FFDECD; color: #770538;  border: 1px solid #FF5B45; }

.download-btn {
  padding: 10px 22px; border: none; border-radius: 6px; cursor: pointer;
  font-size: 14px; font-weight: bold; transition: all 0.25s;
  background: #00AD6E; color: #fff; display: flex; align-items: center; gap: 6px;
}

.download-btn:hover { background: #008a58; color: #00205B; }
.download-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.import-btn {
  padding: 10px 22px; border: none; border-radius: 6px; cursor: pointer;
  font-size: 14px; font-weight: bold; transition: all 0.25s;
  background: #F7911E; color: #00205B; display: flex; align-items: center; gap: 6px;
}

.import-btn:hover { background: #B87333; color: #fff; }
.import-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.import-file-input { display: none; }

.import-panel {
  background: #fff; border: 2px solid #C8C9C7; border-radius: 8px;
  padding: 24px; display: flex; flex-direction: column; gap: 14px;
}

.import-panel-title { font-size: 15px; font-weight: 700; color: #00205B; display: flex; align-items: center; gap: 8px; }
.import-panel-hint { font-size: 12px; color: #555; line-height: 1.5; }

.import-dropzone {
  border: 2px dashed #C8C9C7; border-radius: 8px; padding: 32px 20px;
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  cursor: pointer; transition: all 0.2s; background: #fafbfc;
}

.import-dropzone:hover { border-color: #003594; background: #DBEEFF; }
.import-dropzone.drag-over { border-color: #FFB81C; background: #FFF8E1; }
.import-dropzone-icon { font-size: 36px; opacity: 0.5; }
.import-dropzone-text { font-size: 13px; color: #555; text-align: center; }
.import-dropzone-text strong { color: #003594; cursor: pointer; }
.import-dropzone-formats { font-size: 11px; color: #888; }

.import-or-divider { display: flex; align-items: center; gap: 12px; color: #aaa; font-size: 12px; font-weight: 600; }
.import-or-divider::before, .import-or-divider::after { content: ''; flex: 1; height: 1px; background: #C8C9C7; }

.warning-banner {
  position: fixed; top: 20px; right: 20px; z-index: 9999; max-width: 480px;
  background-color: #FFF8E1; border: 2px solid #FFB81C; border-radius: 8px;
  padding: 16px; box-shadow: 0 4px 16px rgba(0,0,0,0.15); animation: slideIn 0.3s ease-out;
}

.warning-banner-content { display: flex; flex-direction: column; gap: 8px; }
.warning-banner-header { display: flex; justify-content: space-between; align-items: center; font-weight: bold; color: #B87333; font-size: 14px; }
.warning-banner-message { color: #00205B; font-size: 13px; line-height: 1.5; }
.warning-banner-close { cursor: pointer; font-size: 20px; color: #B87333; background: none; border: none; padding: 0; line-height: 1; }
.warning-banner-close:hover { color: #F7911E; }
.warning-columns { margin-top: 4px; padding: 8px 12px; background: #fff; border: 1px solid #FFB81C; border-radius: 4px; font-size: 12px; font-family: monospace; }
.warning-columns-label { font-weight: 600; color: #B87333; margin-bottom: 4px; font-family: Arial, sans-serif; }
.warning-columns-list { color: #00205B; word-break: break-all; }

.table-info {
  font-size: 12px; color: #555; padding: 4px 0;
  display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0;
}
.table-info button { margin-left: 10px ; margin-bottom:10px}

.empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 48px 0; color: #C8C9C7; font-size: 14px; gap: 12px;
}

.empty-icon { font-size: 32px; opacity: 0.5; }

.table-loading-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.85); display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 12px; z-index: 10; border-radius: 6px;
}

.table-loading-text { font-size: 14px; font-weight: 600; color: #003594; }

.error-banner {
  position: fixed; top: 20px; right: 20px; z-index: 10000; max-width: 480px;
  background-color: #FFDECD; border: 2px solid #FF5B45; border-radius: 8px;
  padding: 16px; box-shadow: 0 4px 16px rgba(0,0,0,0.2); animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.error-banner-content { display: flex; flex-direction: column; gap: 8px; }
.error-banner-header { display: flex; justify-content: space-between; align-items: center; font-weight: bold; color: #770538; font-size: 14px; }
.error-banner-message { color: #00205B; font-size: 13px; line-height: 1.5; }
.error-banner-close { cursor: pointer; font-size: 20px; color: #FF5B45; background: none; border: none; padding: 0; line-height: 1; }
.error-banner-close:hover { color: #770538; }

.pagination {
  display: flex; align-items: center; justify-content: center;
  gap: 4px; padding: 12px 0; border-top: 1px solid #e8eaed;
}

.page-btn {
  padding: 6px 12px; font-size: 12px; border: 1px solid #C8C9C7;
  border-radius: 4px; background: #fff; color: #00205B; cursor: pointer; transition: all 0.15s;
}

.page-btn:hover { background: #DBEEFF; border-color: #003594; }
.page-btn.active { background: #003594; color: #fff; border-color: #003594; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.skeleton-item { padding: 14px 20px; border-bottom: 1px solid #f0f0f0; }

.skeleton-line {
  height: 12px;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 4px; margin-bottom: 8px;
}

.skeleton-line.title { width: 80%; height: 14px; }
.skeleton-line.authors { width: 60%; }
.skeleton-line.meta { width: 40%; }

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.mode-chooser {
  flex: 1; display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 32px; padding: 40px 20px;
}

.mode-chooser-main-title { font-size: 40px; font-weight: 700; color: #00205B; text-align: center; }
.mode-chooser-title { font-size: 24px; font-weight: 700; color: #00205B; text-align: center; }

.mode-chooser-subtitle {
  font-size: 14px; color: #555; text-align: center;
  max-width: 500px; line-height: 1.6; margin-top: -16px;
}

.mode-chooser-cards {
  display: flex; gap: 24px; flex-wrap: wrap; justify-content: center; max-width: 1100px; width: 100%;
}

.mode-card {
  flex: 1; min-width: 260px; max-width: 340px; padding: 32px 28px;
  border-radius: 12px; border: 2px solid #C8C9C7; background: #fff;
  cursor: pointer; transition: all 0.25s; display: flex; flex-direction: column;
  align-items: center; gap: 14px; text-align: center;
}

.mode-card:hover { border-color: #003594; box-shadow: 0 4px 20px rgba(0,53,148,0.15); transform: translateY(-2px); }
.mode-card-icon { font-size: 48px; line-height: 1; }
.mode-card-title { font-size: 18px; font-weight: 700; color: #00205B; }
.mode-card-description { font-size: 13px; color: #555; line-height: 1.5; }

.mode-card-badge { margin-top: auto; padding: 5px 14px; border-radius: 16px; font-size: 11px; font-weight: 700; }
.mode-card-badge.search { background: #DBEEFF; color: #003594; }
.mode-card-badge.import { background: #FFDECD; color: #B87333; }

.mode-back-btn {
  padding: 6px 14px; border: none; border-radius: 6px; background: none;
  color: #00205B; font-size: 12px; font-weight: 600; cursor: pointer;
  transition: all 0.2s; display: flex; align-items: center; gap: 6px;
  width:5rem;
}

.mode-back-btn:hover { background: #DBEEFF; border-color: #003594; }

/* ── Chat drawer ── */
.chat-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4); z-index: 15000; animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

.chat-drawer {
  position: fixed; top: 0; right: 0; bottom: 0;
  width: min(520px, 90vw); background: #fff;
  box-shadow: -4px 0 24px rgba(0,0,0,0.2); display: flex;
  flex-direction: column; z-index: 15001; animation: slideInRight 0.25s ease-out;
}

@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }

.chat-drawer-header {
  padding: 16px 20px; background: #003594; color: #fff;
  display: flex; align-items: center; gap: 10px; flex-shrink: 0;
}

.chat-drawer-header-title { font-size: 15px; font-weight: 700; flex: 1; }
.chat-drawer-header-icon { font-size: 20px; }

.chat-drawer-close {
  background: none; border: none; color: #FFB81C; font-size: 22px;
  cursor: pointer; padding: 0; line-height: 1; transition: color 0.2s;
}

.chat-drawer-close:hover { color: #FFE939; }

.chat-drawer-minimize {
  background: none; border: none; color: #FFB81C; font-size: 22px;
  cursor: pointer; padding: 0; line-height: 1; transition: color 0.2s;
}

.chat-drawer-minimize:hover { color: #FFE939; }

/* ── Chat minimized pill — floating chip bottom-right ── */
@keyframes pillSlideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.chat-minimized-pill {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 15000;
  background: #003594;
  color: #fff;
  padding: 12px 18px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0,0,0,0.25);
  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
  animation: pillSlideUp 0.25s ease-out;
  max-width: 340px;
}

.chat-minimized-pill:hover {
  background: #00205B;
  box-shadow: 0 6px 24px rgba(0,0,0,0.35);
  transform: translateY(-2px);
}

.chat-minimized-pill-text {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-minimized-pill-close {
  background: rgba(255,255,255,0.2);
  border: none;
  color: #fff;
  font-size: 16px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
  line-height: 1;
}

.chat-minimized-pill-close:hover { background: #FF5B45; }

/* ── BELL drawer — slides from LEFT, clean slate/white theme ── */
@keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }

.bell-drawer-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(44,62,80,0.45); z-index: 14000; animation: fadeIn 0.2s ease-out;
  backdrop-filter: blur(0px);
}

.bell-drawer {
  position: fixed; top: 0; left: 0; bottom: 0;
  width: min(580px, 92vw); background: #f4f6f8;
  box-shadow: 6px 0 32px rgba(44,62,80,0.18); display: flex;
  flex-direction: column; z-index: 14001; animation: slideInLeft 0.28s cubic-bezier(.25,.46,.45,.94);
}

/* ── Header ── */
.bell-drawer-header {
  padding: 18px 24px;
  background: #2c3e50;
  color: #fff;
  display: flex; align-items: center; gap: 12px; flex-shrink: 0;
  border-bottom: 3px solid #3498db;
}

.bell-drawer-header-title {
  font-size: 14px; font-weight: 700; flex: 1;
  letter-spacing: 0.06em; text-transform: uppercase; color: #ecf0f1;
}

.bell-drawer-close {
  background: none; border: none; color: #95a5a6; font-size: 20px;
  cursor: pointer; padding: 4px; line-height: 1; transition: color 0.2s;
  border-radius: 4px;
}
.bell-drawer-close:hover { color: #fff; background: rgba(255,255,255,0.1); }

.bell-drawer-minimize {
  background: none; border: none; color: #95a5a6; font-size: 20px;
  cursor: pointer; padding: 4px; line-height: 1; transition: color 0.2s;
  border-radius: 4px;
}
.bell-drawer-minimize:hover { color: #fff; background: rgba(255,255,255,0.1); }

/* ── Context card ── */
.bell-context-card {
  padding: 14px 24px;
  background: #fff;
  border-bottom: 1px solid #e8ecef;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(44,62,80,0.06);
}
.bell-context-title {
  font-size: 10px; font-weight: 700; color: #95a5a6;
  margin-bottom: 6px; text-transform: uppercase; letter-spacing: .12em;
}
.bell-context-interaction { font-size: 14px; color: #2c3e50; line-height: 1.5; font-weight: 500; display:flex; align-items:center;}
.bell-context-interaction strong { color: #2c3e50; font-weight: 700; }
.bell-context-detail { font-size: 11px; color: #95a5a6; margin-top: 4px; line-height: 1.6; }

/* ── BELL tabs — pill style ── */
.bell-tabs {
  display: flex; gap: 6px;
  background: #fff;
  padding: 10px 16px;
  border-bottom: 1px solid #e8ecef;
  flex-shrink: 0;
}
.bell-tab {
  flex: 1; padding: 7px 6px; font-size: 11px; font-weight: 600;
  border: 1px solid #dde3e8; background: #f4f6f8; cursor: pointer; color: #7f8c8d;
  border-radius: 20px; margin-bottom: 0;
  transition: all .18s; text-transform: uppercase; letter-spacing: .05em;
}
.bell-tab.active {
  background: #2c3e50; color: #fff; border-color: #2c3e50;
  box-shadow: 0 2px 8px rgba(44,62,80,0.25);
}
.bell-tab:hover:not(.active) { background: #e8ecef; color: #2c3e50; border-color: #bdc3c7; }

/* ── Body ── */
.bell-body {
  padding: 20px 20px; overflow-y: auto; flex: 1; min-height: 0;
  background: #909395; display: flex; flex-direction: column; gap: 12px;
}

/* ── BELL run action row ── */
.bell-action-row {
  padding: 14px 24px; border-top: 1px solid #e8ecef;
  display: flex; gap: 12px; flex-shrink: 0;
  background: #fff; align-items: center;
  box-shadow: 0 -2px 12px rgba(44,62,80,0.07);
}
.bell-run-action-btn {
  padding: 10px 28px; border: none; border-radius: 8px; cursor: pointer;
  font-size: 13px; font-weight: 700; background: #2c3e50; color: #fff;
  transition: all 0.2s; display: flex; align-items: center; gap: 8px;
  box-shadow: 0 2px 8px rgba(44,62,80,0.2); letter-spacing: 0.03em;
}
.bell-run-action-btn:hover:not(:disabled) { background: #34495e; box-shadow: 0 4px 14px rgba(44,62,80,0.3); transform: translateY(-1px); }
.bell-run-action-btn:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }

/* ── BELL minimized pill — LEFT edge tab, slate theme ── */
.bell-minimized-pill {
  position: fixed;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 14000;
  background: #2c3e50;
  color: #fff;
  cursor: pointer;
  box-shadow: 3px 0 18px rgba(44,62,80,0.35);
  transition: background 0.2s, box-shadow 0.2s, width 0.15s;
  animation: pillSlideInLeft 0.25s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 10px;
  border-radius: 0 12px 12px 0;
  width: 40px;
  min-height: 120px;
  justify-content: center;
  border-right: 3px solid #3498db;
}

@keyframes pillSlideInLeft {
  from { transform: translateY(-50%) translateX(-100%); opacity: 0; }
  to   { transform: translateY(-50%) translateX(0);    opacity: 1; }
}

.bell-minimized-pill:hover {
  background: #34495e;
  box-shadow: 4px 0 22px rgba(44,62,80,0.45);
  width: 46px;
}

.bell-minimized-pill-text {
  font-size: 11px;
  font-weight: 700;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 140px;
  letter-spacing: 0.04em;
  color: #bdc3c7;
}

.bell-minimized-pill-close {
  background: rgba(255,255,255,0.12);
  border: none;
  color: #bdc3c7;
  font-size: 13px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
}

.bell-minimized-pill-close:hover { background: #e74c3c; color: #fff; }

/* ── BELL evidence cards — clean modern ── */
.bell-ev-card {
  background: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 3px;
  box-shadow: 0 2px 10px rgba(44,62,80,0.08);
  transition: box-shadow 0.2s;
}
.bell-ev-card:hover { box-shadow: 0 4px 18px rgba(44,62,80,0.13); }
.bell-ev-card-hdr { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.bell-ev-src { font-size: 13px; font-weight: 700; color: #2c3e50; }
.bell-ev-checks { display: flex; gap: 12px; margin-bottom: 6px; }
.bell-ev-check { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #7f8c8d; font-weight: 500; }
.bell-ev-detail { font-size: 12px; color: #7f8c8d; line-height: 1.6; }
.bell-ck-y { color: #27ae60; font-weight: 700; }
.bell-ck-n { color: #e74c3c; font-weight: 700; }
.bell-ck-na { color: #bdc3c7; }
.bell-ev-notfound {
  background: #fff; border: 1px dashed #dde3e8;
  border-radius: 10px; padding: 10px 14px; margin-bottom: 3px; opacity: 0.6;
}
.bell-ev-stub-badge {
  font-size: 9px; font-weight: 700; color: #7f8c8d;
  background: #f4f6f8; border: 1px solid #dde3e8;
  border-radius: 4px; padding: 2px 6px; margin-left: 6px;
}

/* ── BELL score bars — clean modern ── */
.bell-sc-row { margin-bottom: 8px; }
.bell-sc-hdr { display: flex; justify-content: space-between; margin-bottom: 5px; }
.bell-sc-label { font-size: 12px; color: #fff; font-weight: 500; }
.bell-sc-val { font-size: 12px; font-weight: 700;background: #e8ecef; padding:2px 3px; border-radius:5px; }
.bell-sc-track { height: 8px; background: #e8ecef; border-radius: 8px; overflow: hidden; }
.bell-sc-fill { height: 100%; border-radius: 8px; }

/* ── BELL badges ── */
.bell-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 10px; font-weight: 700; letter-spacing: .05em; white-space: nowrap;
}
.bell-b-high { background: #d5f5e3; color: #1e8449; border: 1px solid #82e0aa; }
.bell-b-med  { background: #fef9e7; color: #b7770d; border: 1px solid #f9ca24; }
.bell-b-low  { background: #fadbd8; color: #922b21; border: 1px solid #f1948a; }
.bell-b-info { background: #d6eaf8; color: #1a5276; border: 1px solid #85c1e9; }
.bell-b-neutral { background: #f2f3f4; color: #7f8c8d; border: 1px solid #d5d8dc; }

/* ── BELL CoT ── */
.bell-cot-block {
  border-radius: 12px; overflow: hidden;
  border: none; margin-bottom: 0;
  box-shadow: 0 2px 10px rgba(44,62,80,0.08);
}
.bell-cot-hdr {
  display: flex; align-items: center; gap: 8px; padding: 10px 14px;
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em;
}
.bell-cot-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.bell-cot-body {
  padding: 14px 16px; font-size: 12px; line-height: 1.8;
  white-space: pre-wrap; border-top: 1px solid rgba(44,62,80,0.06);
}

/* ── BELL flags/contradictions ── */
.bell-flag-banner {
  background: #fef9e7; border: none; border-radius: 10px;
  padding: 12px 14px; margin-top: 0;
  box-shadow: 0 2px 8px rgba(185,119,14,0.12);
  margin-top:10px;
}
.bell-flag-title { font-size: 11px; font-weight: 700; color: #b7770d; margin-bottom: 5px; }
.bell-flag-item { font-size: 11px; color: #b7770d; margin-bottom: 2px; }
.bell-conflict-banner {
  background: #fdedec; border: none; border-radius: 10px;
  padding: 12px 14px; margin-top: 0;
  box-shadow: 0 2px 8px rgba(231,76,60,0.12);
}
.bell-conflict-title { font-size: 11px; font-weight: 700; color: #922b21; margin-bottom: 5px; }
.bell-conflict-text { font-size: 11px; color: #922b21; line-height: 1.5; }

/* ── BELL grounding card ── */
.bell-grounding-card {
  background: #d6eaf8; border: none; border-radius: 10px;
  padding: 12px 14px; margin-bottom: 10px;
}
.bell-grounding-label {
  font-weight: 700; color: #1a5276; margin-bottom: 4px;
  font-size: 10px; text-transform: uppercase; letter-spacing: .08em;
}
.bell-grounding-val { font-size: 11px; color: #2c3e50; line-height: 1.6; }

.bell-pending-hint {
  font-size: 13px; color: #bdc3c7; padding: 40px 0;
  text-align: center; font-style: italic; letter-spacing: 0.02em;
}

.chat-context-card { padding: 12px 20px; background: #DBEEFF; border-bottom: 1px solid #C8C9C7; flex-shrink: 0; }

.chat-context-title {
  font-size: 12px; font-weight: 700; color: #003594; margin-bottom: 6px;
  text-transform: uppercase; letter-spacing: 0.05em;
}

.chat-context-interaction { font-size: 13px; color: #00205B; line-height: 1.5; }
.chat-context-interaction strong { color: #003594; }
.chat-context-detail { font-size: 11px; color: #555; margin-top: 4px; }

.chat-messages {
  flex: 1; overflow-y: auto; padding: 16px 20px;
  display: flex; flex-direction: column; gap: 12px; min-height: 0;
}

.chat-msg {
  max-width: 85%; padding: 10px 14px; border-radius: 12px;
  font-size: 13px; line-height: 1.5; word-wrap: break-word;
}

.chat-msg.user { align-self: flex-end; background: #003594; color: #fff; border-bottom-right-radius: 4px; }
.chat-msg.assistant { align-self: flex-start; background: #f0f2f5; color: #000; border-bottom-left-radius: 4px; }
.chat-msg.assistant .chat-msg-label { font-size: 10px; font-weight: 700; color: #B87333; margin-bottom: 4px; display: flex; align-items: center; gap: 4px; }

.chat-msg.system {
  align-self: center; background: #FFF8E1; color: #B87333; font-size: 12px;
  border-radius: 8px; border: 1px solid #FFB81C; text-align: center; max-width: 90%;
}

.chat-typing { align-self: flex-start; display: flex; gap: 4px; padding: 12px 16px; }

.chat-typing-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #C8C9C7; animation: typingBounce 1.2s infinite;
}

.chat-typing-dot:nth-child(2) { animation-delay: 0.2s; }
.chat-typing-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes typingBounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}

.chat-input-row {
  padding: 12px 20px; border-top: 1px solid #C8C9C7;
  display: flex; gap: 8px; flex-shrink: 0; background: #fafbfc;
}

.chat-input {
  flex: 1; padding: 10px 14px; font-size: 13px; border: 2px solid #C8C9C7;
  border-radius: 8px; outline: none; transition: border-color 0.2s;
  resize: none; font-family: inherit; min-height: 40px; max-height: 100px;
}

.chat-input:focus { border-color: #003594; }

.chat-send-btn {
  padding: 8px 16px; border: none; border-radius: 8px; background: #003594;
  color: #fff; font-weight: 700; font-size: 13px; cursor: pointer;
  transition: background 0.2s; align-self: flex-end; white-space: nowrap;
}

.chat-send-btn:hover { background: #00205B; }
.chat-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.chat-suggestions { display: flex; gap: 6px; flex-wrap: wrap; padding: 0 20px 12px; flex-shrink: 0; }

.chat-suggestion-btn {
  padding: 5px 12px; font-size: 12px; border: 1px solid #003594;
  border-radius: 16px; background: #fff; color: #003594; cursor: pointer; transition: all 0.15s;
}

.chat-suggestion-btn:hover { background: #DBEEFF; }
.button-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0; }

.method-change-modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); z-index: 20000; display: flex;
  align-items: center; justify-content: center; animation: fadeIn 0.2s ease-out;
}

.method-change-modal {
  background: #fff; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  max-width: 460px; width: 90%; overflow: hidden; animation: modalSlideIn 0.25s ease-out;
}

@keyframes modalSlideIn {
  from { transform: translateY(-20px) scale(0.97); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}

.method-change-modal-header {
  padding: 20px 24px 16px; background: #FFF8E1; border-bottom: 2px solid #FFB81C;
  display: flex; align-items: center; gap: 10px;
}

.method-change-modal-icon { font-size: 28px; line-height: 1; }
.method-change-modal-title { font-size: 17px; font-weight: 700; color: #B87333; }

.method-change-modal-body {
  padding: 20px 24px; font-size: 14px; color: #00205B; line-height: 1.6;
}

.method-change-modal-body strong { color: #003594; }

.method-change-modal-count {
  display: inline-block; background: #FFDECD; color: #B87333;
  font-weight: 700; padding: 2px 10px; border-radius: 4px; font-size: 13px; margin-left: 4px;
}

.method-change-modal-footer {
  padding: 16px 24px 20px; display: flex; justify-content: flex-end;
  gap: 10px; background: #fafbfc; border-top: 1px solid #e8eaed;
}

.modal-btn { padding: 10px 22px; border-radius: 6px; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.2s; border: none; }
.modal-btn-cancel { background: #e8eaed; color: #00205B; }
.modal-btn-cancel:hover { background: #C8C9C7; }
.modal-btn-confirm { background: #FF5B45; color: #fff; }
.modal-btn-confirm:hover { background: #d43f2a; }

.statement-modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); z-index: 20000; display: flex;
  align-items: center; justify-content: center; animation: fadeIn 0.2s ease-out;
}

.statement-modal {
  background: #fff; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  max-width: 640px; width: 90%; max-height: 70vh;
  display: flex; flex-direction: column; overflow: hidden; animation: modalSlideIn 0.25s ease-out;
}

.statement-modal-header {
  padding: 16px 24px; background: #003594; color: #fff;
  display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
}

.statement-modal-header-title { font-size: 15px; font-weight: 700; display: flex; align-items: center; gap: 8px; }

.statement-modal-close {
  background: none; border: none; color: #FFB81C; font-size: 22px;
  cursor: pointer; padding: 0; line-height: 1; transition: color 0.2s;
}

.statement-modal-close:hover { color: #FFE939; }
.statement-modal-body { padding: 24px; overflow-y: auto; flex: 1; min-height: 0; }
.statement-modal-text { font-size: 14px; color: #333; line-height: 1.8; white-space: pre-wrap; word-break: break-word; }
.statement-modal-meta { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e8eaed; display: flex; gap: 8px; flex-wrap: wrap; }
.statement-modal-tag { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 4px; }
.statement-modal-tag.regulator { background: #DBEEFF; color: #003594; }
.statement-modal-tag.regulated { background: #DBEEFF; color: #003594; }
.statement-modal-tag.sign { background: #FFDECD; color: #003594; }
.statement-modal-tag.paperid { background: #FFDECD; color: #003594; }
`;

// ─────────────────────────────────────────────
// TABLE CONSTANTS
// ─────────────────────────────────────────────
const ROW_HEIGHT = 36;
const COL_MIN_WIDTH = 150;
const ACTIONS_COL_WIDTH = 210; // wider to fit BELL button
const OVERSCAN = 15;
const ROW_NUM_WIDTH = 56; // the "#" column
// This column can never be hidden, so the table always identifies its rows.
const PINNED_COLUMN = "Regulator Name";

// ─────────────────────────────────────────────
// FIELD → LABEL MAP
// ─────────────────────────────────────────────
const FIELD_TO_LABEL_MAP = {
  "Regulator Name": "Regulator Name",
  "Regulator Type": "Regulator Type",
  "Regulator Subtype": "Regulator Subtype",
  "Regulator Compartment": "Regulator Compartment",
  "Regulated Name": "Regulated Name",
  "Regulated Type": "Regulated Type",
  "Regulated Subtype": "Regulated Subtype",
  "Regulated Compartment": "Regulated Compartment",
  Sign: "Sign",
  "Connection Type": "Connection Type",
  Mechanism: "Mechanism",
  Site: "Site",
  "Cell Line": "Cell Line",
  "Cell Type": "Cell Type",
  "Tissue Type": "Tissue Type",
  Organism: "Organism",
};

// ─────────────────────────────────────────────
// EXPANDABLE CELL
// ─────────────────────────────────────────────
const ExpandableCell = ({ value, row, savedData, onSaveResult }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  if (!value) return null;

  const savedAnnotations = savedData?.annotations || [];
  const savedGeneralNote = savedData?.generalNote || "";
  const savedEvaluation = savedData?.evaluation || null;
  const hasAnnotations = savedAnnotations.length > 0;

  const initialAnnotations = hasAnnotations
    ? savedAnnotations
    : buildAutoAnnotations(
        String(value),
        row,
        BIO_ANNOTATION_LABELS,
        FIELD_TO_LABEL_MAP,
      );

  return (
    <>
      <span
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          cursor: "pointer",
          textDecoration: hovered ? "underline solid" : "underline dotted",
          textUnderlineOffset: "3px",
          color: hovered ? "#FFB81C" : hasAnnotations ? "#F57C00" : "#003594",
          background: hovered ? "rgba(255,184,28,0.1)" : "transparent",
          borderRadius: 4,
          padding: hovered ? "1px 4px" : "1px 0",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          transition: "all 0.15s ease",
        }}
      >
        {hasAnnotations && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: hovered ? "#FFB81C" : "#FFB74D",
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              flexShrink: 0,
              transition: "background 0.15s ease",
            }}
          >
            {savedAnnotations.length}
          </span>
        )}
        {value}
      </span>

      <TextAnnotatorModal
        isOpen={open}
        onClose={() => setOpen(false)}
        text={String(value)}
        labels={BIO_ANNOTATION_LABELS}
        initialAnnotations={initialAnnotations}
        initialGeneralNote={savedGeneralNote}
        initialEvaluation={savedEvaluation}
        rowData={row}
        onSave={(result) => {
          if (onSaveResult) onSaveResult(result);
        }}
      />
    </>
  );
};

// ─────────────────────────────────────────────
// BELL SMALL COMPONENTS
// ─────────────────────────────────────────────
const BellCk = ({ v }) =>
  v == null ? (
    <span className="bell-ck-na">—</span>
  ) : v ? (
    <span className="bell-ck-y">✓</span>
  ) : (
    <span className="bell-ck-n">✗</span>
  );

const BellScoreBar = ({ label, value }) => {
  // S2 is null when no database could judge sign, mechanism, or connection type
  if (value == null) {
    return (
      <div className="bell-sc-row">
        <div className="bell-sc-hdr">
          <span className="bell-sc-label">{label}</span>
          <span className="bell-sc-val" style={{ color: "#7f8c8d" }}>
            n/a
          </span>
        </div>
        <div className="bell-sc-track" />
      </div>
    );
  }
  const cl = value >= 0.8 ? "#3498db" : value >= 0.5 ? "#f39c12" : "#e74c3c";
  const tc = value >= 0.8 ? "#1a5276" : value >= 0.5 ? "#b7770d" : "#922b21";
  return (
    <div className="bell-sc-row">
      <div className="bell-sc-hdr">
        <span className="bell-sc-label">{label}</span>
        <span className="bell-sc-val" style={{ color: tc }}>
          {value.toFixed(2)}
        </span>
      </div>
      <div className="bell-sc-track">
        <div
          className="bell-sc-fill"
          style={{ width: `${Math.round(value * 100)}%`, background: cl }}
        />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// BELL DRAWER COMPONENT
// ─────────────────────────────────────────────
const BellDrawer = ({
  interaction,
  bellResult,
  isRunning,
  pipeStep,
  onRun,
  onClose,
  onMinimize,
}) => {
  const [activeTab, setActiveTab] = useState("evidence");
  const PIPE_STEPS = ["Normalize", "Route", "Query", "Score", "Explain"];

  const reg = interaction["Regulator Name"] || "?";
  const tgt = interaction["Regulated Name"] || "?";
  const sign = (interaction["Sign"] || "").toLowerCase();
  const mech = interaction["Mechanism"] || "";

  const confClass =
    bellResult?.confidence === "HIGH"
      ? "high"
      : bellResult?.confidence === "MEDIUM"
        ? "med"
        : "low";

  return (
    <>
      <div className="bell-drawer-overlay" onClick={onMinimize} />
      <div className="bell-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bell-drawer-header">
          <span className="bell-drawer-header-title">
            Evidence Verification (BELL)
          </span>
          <button
            className="bell-drawer-minimize"
            onClick={onMinimize}
            title="Minimize"
          >
            −
          </button>
          <button className="bell-drawer-close" onClick={onClose} title="Close">
            ×
          </button>
        </div>

        {/* Context card */}
        <div className="bell-context-card">
          <div className="bell-context-title">Interaction</div>
          <div className="bell-context-interaction">
            <strong>{reg}</strong>
            <span
              style={{
                margin: "0 6px",
                color: sign.includes("pos") ? "#00AD6E" : "#FF5B45",
                fontSize: "16px",
              }}
            >
              {sign.includes("pos") ? "→" : sign.includes("neg") ? "→" : "→"}
            </span>
            <strong>{tgt}</strong>
            {bellResult && (
              <span
                className={`bell-badge bell-b-${confClass}`}
                style={{ marginLeft: 10 }}
              >
                {bellResult.confidence}
              </span>
            )}
          </div>
          <div className="bell-context-detail">
            {[
              mech && `Mechanism: ${mech}`,
              interaction["Cell Line"] && `Cell: ${interaction["Cell Line"]}`,
              interaction["Organism"] && `Org: ${interaction["Organism"]}`,
            ]
              .filter(Boolean)
              .join("  ·  ")}
          </div>
        </div>

        {/* Pipeline progress strip (shown while running) */}
        {isRunning && (
          <div
            style={{
              padding: "10px 20px",
              background: "#2c3e50",
              borderBottom: "1px solid rgba(52,152,219,0.3)",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#3498db",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              Running pipeline…
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {PIPE_STEPS.map((s, i) => {
                const done = pipeStep > i;
                const active = pipeStep === i;
                return (
                  <React.Fragment key={s}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 3,
                        flex: 1,
                      }}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          border: "2px solid",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 700,
                          background: done
                            ? "#3498db"
                            : active
                              ? "#f39c12"
                              : "#e8ecef",
                          borderColor: done
                            ? "#3498db"
                            : active
                              ? "#f39c12"
                              : "#bdc3c7",
                          color: done || active ? "#fff" : "#95a5a6",
                          transition: "all .3s",
                        }}
                      >
                        {done ? "✓" : i + 1}
                      </div>
                      <span
                        style={{
                          fontSize: 9,
                          color: done
                            ? "#1a5276"
                            : active
                              ? "#b7770d"
                              : "#bdc3c7",
                          fontWeight: 600,
                        }}
                      >
                        {s}
                      </span>
                    </div>
                    {i < PIPE_STEPS.length - 1 && (
                      <div
                        style={{
                          flex: 1,
                          height: 2,
                          background: done ? "#3498db" : "#e8ecef",
                          marginTop: -12,
                          transition: "background .3s",
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bell-tabs">
          {["evidence", "scores", "explanation", "output"].map((t) => (
            <button
              key={t}
              className={`bell-tab${activeTab === t ? " active" : ""}`}
              onClick={() => setActiveTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="bell-body">
          {/* ── EVIDENCE ── */}
          {activeTab === "evidence" && (
            <div>
              {(bellResult?.grounding || bellResult?.normalized) && (
                <div className="bell-grounding-card">
                  <div className="bell-grounding-label">Grounded IDs</div>
                  <div
                    className="bell-grounding-val"
                    style={{ marginBottom: 3 }}
                  >
                    <b style={{ color: "#2c3e50" }}>{reg}:</b>{" "}
                    {bellResult.grounding
                      ? `${bellResult.grounding.regulator.db}:${bellResult.grounding.regulator.db_id}`
                      : bellResult.normalized?.regulator || "—"}
                  </div>
                  <div className="bell-grounding-val">
                    <b style={{ color: "#2c3e50" }}>{tgt}:</b>{" "}
                    {bellResult.grounding
                      ? `${bellResult.grounding.regulated.db}:${bellResult.grounding.regulated.db_id}`
                      : bellResult.normalized?.regulated || "—"}
                  </div>
                </div>
              )}
              {interaction["Statements"] && (
                <div
                  style={{
                    background: "#fff",
                    border: "none",
                    borderRadius: 12,
                    padding: "14px 16px",
                    marginBottom: "10px",
                    boxShadow: "0 2px 10px rgba(44,62,80,0.08)",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#7f8c8d",
                      marginBottom: 9,
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: ".1em",
                    }}
                  >
                    Source Statement
                  </div>
                  <div
                    style={{ fontSize: 13, color: "#2c3e50", lineHeight: 1.7 }}
                  >
                    {interaction["Statements"]}
                  </div>
                  {interaction["Paper IDs"] && (
                    <div
                      style={{
                        marginTop: 5,
                        fontSize: 11,
                        color: "#3498db",
                        fontWeight: 600,
                      }}
                    >
                      {`Paper ID: ${interaction["Paper IDs"]}`}
                    </div>
                  )}
                </div>
              )}
              {!bellResult ? (
                <div className="bell-pending-hint">
                  Click ▶ Run below to retrieve evidence.
                </div>
              ) : (
                bellResult.evidence?.map((ev) => {
                  if (!ev.found)
                    return (
                      <div key={ev.source} className="bell-ev-notfound">
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span
                            className="bell-ev-src"
                            style={{ color: "#999" }}
                          >
                            {ev.source}
                          </span>
                          <span style={{ fontSize: 11, color: "#bdc3c7" }}>
                            not found
                          </span>
                        </div>
                      </div>
                    );
                  return (
                    <div key={ev.source} className="bell-ev-card">
                      <div className="bell-ev-card-hdr">
                        <span className="bell-ev-src">{ev.source}</span>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color:
                              ev.score >= 0.8
                                ? "#00693e"
                                : ev.score >= 0.5
                                  ? "#B87333"
                                  : "#770538",
                          }}
                        >
                          {ev.score.toFixed(2)}
                        </span>
                      </div>
                      <div className="bell-ev-checks">
                        {[
                          ["Directed interaction", ev.dir], // was "Dir"
                          ["Sign match", ev.sign],
                          ["Mechanism match", ev.mech],
                          ["Connection Type match", ev.directness_match],
                        ].map(([l, v]) => (
                          <div key={l} className="bell-ev-check">
                            {l} <BellCk v={v} />
                          </div>
                        ))}
                        {/* <div className="bell-ev-check">
                          Ctx{" "}
                          {ev.ctx === "partial" ? (
                            <span
                              className="bell-badge bell-b-med"
                              style={{ fontSize: 9, padding: "1px 4px" }}
                            >
                              partial
                            </span>
                          ) : ev.ctx === null ? (
                            <span className="bell-ck-na">—</span>
                          ) : (
                            <span className="bell-ck-y">✓</span>
                          )}
                        </div> */}
                      </div>
                      <div className="bell-ev-detail">{ev.detail}</div>
                    </div>
                  );
                })
              )}
              {bellResult?.contradictions?.length > 0 && (
                <div className="bell-conflict-banner">
                  <div className="bell-conflict-title">
                    ⚠ Contradiction detected
                  </div>
                  {bellResult.contradictions.map((c, i) => (
                    <div key={i} className="bell-conflict-text">
                      {c.detail}
                    </div>
                  ))}
                </div>
              )}
              {bellResult?.flags?.length > 0 && (
                <div className="bell-flag-banner">
                  <div className="bell-flag-title">Flags</div>
                  {bellResult.flags.map((f) => (
                    <div key={f} className="bell-flag-item">
                      • {f.replace(/_/g, " ")}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── SCORES ── */}
          {activeTab === "scores" && (
            <div>
              {!bellResult ? (
                <div className="bell-pending-hint">
                  Run pipeline to compute scores.
                </div>
              ) : (
                <>
                  <BellScoreBar
                    label="S1 — Source coverage"
                    value={bellResult.scores.S1}
                  />
                  <BellScoreBar
                    label="S2 — Attribute match"
                    value={bellResult.scores.S2}
                  />
                  <BellScoreBar
                    label="S3 — INDRA belief"
                    value={bellResult.scores.S3}
                  />
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: "1px solid #C8C9C7",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: 12, color: "#fff" }}>
                        S4 — Contradiction flag
                      </span>
                      <span
                        className={`bell-badge bell-b-${bellResult.scores.S4 === 0 ? "high" : bellResult.scores.S4 === 1 ? "med" : "low"}`}
                      >
                        {bellResult.scores.S4 === 0
                          ? "none"
                          : bellResult.scores.S4 === 1
                            ? "partial"
                            : "unresolved"}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: 12,
                      padding: "10px 12px",
                      background: "#d6eaf8",
                      border: "none",
                      borderRadius: 12,
                      boxShadow: "0 2px 10px rgba(44,62,80,0.08)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#1a5276",
                      }}
                    >
                      Overall confidence
                    </span>
                    <span className={`bell-badge bell-b-${confClass}`}>
                      {bellResult.confidence}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── EXPLANATION ── */}
          {activeTab === "explanation" && (
            <div>
              {!bellResult ? (
                <div className="bell-pending-hint">
                  Run pipeline to generate explanation.
                </div>
              ) : (
                <>
                  <div className="bell-cot-block">
                    <div
                      className="bell-cot-hdr"
                      style={{ background: "#2c3e50", color: "#bdc3c7" }}
                    >
                      <span
                        className="bell-cot-dot"
                        style={{ background: "#3498db" }}
                      />
                      Query sent to LLM
                    </div>
                    <div
                      className="bell-cot-body"
                      style={{
                        background: "#f8f9fa",
                        color: "#000",
                        fontFamily: "'Courier New', monospace",
                        fontSize: 12,
                        marginBottom: "10px",
                      }}
                    >
                      {typeof bellResult.cot === "object"
                        ? bellResult.cot.query
                        : bellResult.cot}
                    </div>
                  </div>
                  <div className="bell-cot-block">
                    <div
                      className="bell-cot-hdr"
                      style={{ background: "#eaf4fb", color: "#1a5276" }}
                    >
                      <span
                        className="bell-cot-dot"
                        style={{ background: "#2dd4bf" }}
                      />
                      LLM response
                    </div>
                    <div
                      className="bell-cot-body"
                      style={{
                        background: "#fff",
                        color: "#2c3e50",
                        fontSize: 13,
                        lineHeight: 1.8,
                      }}
                    >
                      {typeof bellResult.cot === "object"
                        ? bellResult.cot.answer
                        : ""}
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 10,
                      borderTop: "1px solid #C8C9C7",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: ".07em",
                        color: "#fff",
                        marginBottom: 6,
                      }}
                    >
                      Recommended Action
                    </div>
                    <span
                      className={`bell-badge bell-b-${bellResult?.action === "Accept" || bellResult?.action === "Accept with enrichment" ? "high" : bellResult?.action?.startsWith("Reject") ? "low" : "med"}`}
                    >
                      {bellResult.action}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── OUTPUT ── */}
          {activeTab === "output" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* Interaction summary */}
              <div
                style={{
                  background: "#fafbfc",
                  border: "1px solid #C8C9C7",
                  borderRadius: 5,
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#7f8c8d",
                    textTransform: "uppercase",
                    letterSpacing: ".1em",
                    marginBottom: 8,
                  }}
                >
                  Interaction Summary
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "3px 10px",
                  }}
                >
                  {[
                    ["Regulator", interaction["Regulator Name"]],
                    ["Regulated", interaction["Regulated Name"]],
                    ["Sign", interaction["Sign"]],
                    ["Mechanism", interaction["Mechanism"]],
                    ["Connection Type", interaction["Connection Type"]],
                    ["Cell Line", interaction["Cell Line"]],
                    ["Tissue Type", interaction["Tissue Type"]],
                    ["Organism", interaction["Organism"]],
                    ["Source", interaction["Source"]],
                    ["Score", interaction["Score"]],
                  ]
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <div
                        key={k}
                        style={{
                          display: "flex",
                          gap: 4,
                          alignItems: "baseline",
                        }}
                      >
                        <span
                          style={{ fontSize: 10, color: "#888", flexShrink: 0 }}
                        >
                          {k}:
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: "#00205B",
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {v}
                        </span>
                      </div>
                    ))}
                </div>
                {interaction["Statements"] && (
                  <div style={{ marginTop: 6 }}>
                    <span style={{ fontSize: 10, color: "#888" }}>
                      Statement:{" "}
                    </span>
                    <span
                      style={{ fontSize: 11, color: "#333", lineHeight: 1.6 }}
                    >
                      {interaction["Statements"]}
                    </span>
                  </div>
                )}
              </div>

              {/* Evidence summary */}
              {bellResult?.evidence?.length > 0 && (
                <div
                  style={{
                    background: "#fafbfc",
                    border: "1px solid #C8C9C7",
                    borderRadius: 5,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#7f8c8d",
                      textTransform: "uppercase",
                      letterSpacing: ".1em",
                      marginBottom: 8,
                    }}
                  >
                    Evidence Summary
                  </div>
                  {bellResult.evidence.map((ev) => (
                    <div
                      key={ev.source}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#00205B",
                          minWidth: 115,
                          flexShrink: 0,
                        }}
                      >
                        {ev.source}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: "#555",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ev.detail}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          flexShrink: 0,
                          color:
                            ev.score >= 0.8
                              ? "#00693e"
                              : ev.score >= 0.5
                                ? "#B87333"
                                : "#770538",
                        }}
                      >
                        {ev.score.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Confidence */}
              {bellResult && (
                <div
                  style={{
                    background: "#fafbfc",
                    border: "1px solid #C8C9C7",
                    borderRadius: 5,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#7f8c8d",
                      textTransform: "uppercase",
                      letterSpacing: ".1em",
                      marginBottom: 8,
                    }}
                  >
                    Confidence &amp; Scores
                  </div>
                  {[
                    ["S1 — Source coverage", bellResult.scores.S1],
                    ["S2 — Attribute match", bellResult.scores.S2],
                    ["S3 — INDRA belief", bellResult.scores.S3],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 5,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          color: "#555",
                          minWidth: 130,
                          flexShrink: 0,
                        }}
                      >
                        {label}
                      </span>
                      <div
                        style={{
                          flex: 1,
                          height: 8,
                          background: "#e8ecef",
                          borderRadius: 8,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.round((value ?? 0) * 100)}%`,
                            height: "100%",
                            borderRadius: 8,
                            background:
                              value >= 0.8
                                ? "#3498db"
                                : value >= 0.5
                                  ? "#f39c12"
                                  : "#e74c3c",
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          minWidth: 30,
                          textAlign: "right",
                          flexShrink: 0,
                          color:
                            value >= 0.8
                              ? "#1a5276"
                              : value >= 0.5
                                ? "#b7770d"
                                : "#922b21",
                        }}
                      >
                        {value == null ? "n/a" : value.toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 11, color: "#555" }}>
                      Confidence:
                    </span>
                    <span className={`bell-badge bell-b-${confClass}`}>
                      {bellResult.confidence}
                    </span>
                    {bellResult.action && (
                      <>
                        <span
                          style={{ fontSize: 11, color: "#555", marginLeft: 8 }}
                        >
                          Action:
                        </span>
                        <span
                          className={`bell-badge bell-b-${bellResult.action === "Accept" || bellResult.action === "Accept with enrichment" ? "high" : bellResult.action?.startsWith("Reject") ? "low" : "med"}`}
                        >
                          {bellResult.action}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {!bellResult && (
                <div className="bell-pending-hint">
                  Run pipeline to populate evidence &amp; scores.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Run button */}
        <div className="bell-action-row">
          <button
            className="bell-run-action-btn"
            onClick={onRun}
            disabled={isRunning}
          >
            {isRunning ? (
              <>
                <ReactLoading type="spin" color="#fff" height={16} width={16} />{" "}
                Running…
              </>
            ) : bellResult ? (
              "▶ Re-run "
            ) : (
              "▶ Run Pipeline (BELL)"
            )}
          </button>
          {bellResult && (
            <span style={{ fontSize: 12, color: "#555" }}>
              Last run:{" "}
              <span className={`bell-badge bell-b-${confClass}`}>
                {bellResult.confidence}
              </span>
            </span>
          )}
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// COLUMN FILTER DROPDOWN (show / hide table columns)
// ─────────────────────────────────────────────
const ColumnFilterDropdown = ({
  columns,
  hiddenColumns,
  pinnedColumn,
  onToggle,
  onShowAll,
  onHideAll,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [search, setSearch] = useState("");
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectAllRef = useRef(null);

  const updateCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 220),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateCoords();
    setSearch("");
    const focusTimer = setTimeout(() => searchInputRef.current?.focus(), 0);
    const handleOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    // scrolling inside the list must not move or close it
    const handleScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      updateCoords();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open, updateCoords]);

  // The pinned column never counts as hideable.
  const toggleableColumns = columns.filter((c) => c !== pinnedColumn);
  const hiddenCount = toggleableColumns.filter((c) => hiddenColumns.has(c)).length;
  const allSelected = hiddenCount === 0;
  const noneSelected = hiddenCount === toggleableColumns.length;
  const visibleCount = columns.length - hiddenCount;

  // Tri-state: indeterminate when some (but not all) columns are visible.
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = !allSelected && !noneSelected;
    }
  });

  const filteredColumns = columns.filter((c) =>
    c.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        className="col-filter-btn"
        onClick={() => setOpen((o) => !o)}
      >
        Columns ({visibleCount}/{columns.length})
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            className="edit-multiselect-panel"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              minWidth: coords.width,
              maxHeight: 320,
            }}
          >
            <div className="edit-multiselect-search-wrap">
              <input
                ref={searchInputRef}
                type="text"
                className="edit-multiselect-search"
                placeholder="Search columns..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setOpen(false);
                }}
              />
            </div>

            <label
              className="edit-multiselect-option"
              style={{
                fontWeight: 700,
                borderBottom: "1px solid #C8C9C7",
                color: "#00205B",
              }}
            >
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={() => (allSelected ? onHideAll() : onShowAll())}
              />
              <span>{allSelected ? "Deselect all" : "Select all"}</span>
            </label>

            {filteredColumns.length === 0 ? (
              <div className="edit-multiselect-empty">No matches</div>
            ) : (
              filteredColumns.map((col) => {
                const isPinned = col === pinnedColumn;
                return (
                  <label
                    key={col}
                    className="edit-multiselect-option"
                    style={
                      isPinned
                        ? { opacity: 0.7, cursor: "not-allowed" }
                        : undefined
                    }
                    title={isPinned ? "Always visible" : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={isPinned || !hiddenColumns.has(col)}
                      disabled={isPinned}
                      onChange={() => onToggle(col)}
                    />
                    <span>
                      {col}
                      {isPinned && (
                        <span
                          style={{
                            fontSize: 10,
                            color: "#B87333",
                            marginLeft: 6,
                          }}
                        >
                          🔒 always visible
                        </span>
                      )}
                    </span>
                  </label>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

const columnFilterStyles = `
.col-filter-btn {
  padding: 4px 12px; border: 1px solid #003594; border-radius: 6px; background: #fff;
  color: #003594; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s;
}
.col-filter-btn:hover { background: #DBEEFF; border-color: #00205B; }
.virtual-table-rownum { justify-content: center; font-weight: 600; color: #555; }
`;

// ─────────────────────────────────────────────
// INTERACTION TABLE
// ─────────────────────────────────────────────
const InteractionTable = ({
  data,
  setData,
  columns,
  isLoading,
  onChat,
  chatActiveIndex,
  annotationsMap,
  onAnnotationsChange,
  onBell,
  bellActiveIndex,
  bellResultsMap,
  runningBellIdx,
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editBuffer, setEditBuffer] = useState({});
  const [filterText, setFilterText] = useState({});
  const [hiddenColumns, setHiddenColumns] = useState(() => new Set());
  const pinnedColumn = columns.includes(PINNED_COLUMN)
    ? PINNED_COLUMN
    : columns[0];
  const visibleColumns = useMemo(
    () => columns.filter((c) => c === pinnedColumn || !hiddenColumns.has(c)),
    [columns, hiddenColumns, pinnedColumn],
  );
  const toggleColumn = useCallback(
    (col) => {
      if (col === pinnedColumn) return;
      setHiddenColumns((prev) => {
        const next = new Set(prev);
        if (next.has(col)) next.delete(col);
        else next.add(col);
        return next;
      });
    },
    [pinnedColumn],
  );
  const showAllColumns = useCallback(() => setHiddenColumns(new Set()), []);
  const hideAllColumns = useCallback(
    () => setHiddenColumns(new Set(columns.filter((c) => c !== pinnedColumn))),
    [columns, pinnedColumn],
  );
  const headerScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const resizeObserverRef = useRef(null);

  // Filtered rows + map from filtered index -> original index.
  // Computed synchronously so edits (e.g. annotation field updates) show instantly.
  const { filteredData, filteredIndexMap } = useMemo(() => {
    const filtered = [];
    const map = new Map();
    data.forEach((row, i) => {
      const matches = Object.keys(filterText).every((key) => {
        const fv = (filterText[key] || "").trim().toLowerCase();
        if (!fv || hiddenColumns.has(key)) return true;
        return String(row[key] || "")
          .toLowerCase()
          .includes(fv);
      });
      if (matches) {
        map.set(filtered.length, i);
        filtered.push(row);
      }
    });
    return { filteredData: filtered, filteredIndexMap: map };
  }, [data, filterText, hiddenColumns]);

  useEffect(() => {
    const el = bodyScrollRef.current;
    if (!el) return;
    const measure = () => setContainerHeight(el.clientHeight);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      resizeObserverRef.current = new ResizeObserver(measure);
      resizeObserverRef.current.observe(el);
    }
    return () => {
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    if (bodyScrollRef.current) {
      requestAnimationFrame(() => {
        if (bodyScrollRef.current)
          setContainerHeight(bodyScrollRef.current.clientHeight);
      });
    }
  }, [data.length]);

  const handleBodyScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
    if (headerScrollRef.current)
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
  }, []);

  const handleFilterChange = useCallback((header, value) => {
    setFilterText((prev) => ({ ...prev, [header]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterText({});
    setScrollTop(0);
    if (bodyScrollRef.current) bodyScrollRef.current.scrollTop = 0;
  }, []);

  const handleEdit = useCallback(
    (idx) => {
      setEditingIndex(idx);
      setEditBuffer({ ...data[idx] });
    },
    [data],
  );

  const handleSave = useCallback(() => {
    const d = [...data];
    d[editingIndex] = { ...editBuffer };
    setData(d);
    setEditingIndex(null);
    setEditBuffer({});
  }, [data, editingIndex, editBuffer, setData]);

  const handleCancel = useCallback(() => {
    setEditingIndex(null);
    setEditBuffer({});
  }, []);

  const handleDelete = useCallback(
    (idx) => {
      if (window.confirm("Delete this interaction?")) {
        setData(data.filter((_, j) => j !== idx));
        onAnnotationsChange((prev) => {
          const updated = {};
          Object.entries(prev).forEach(([key, value]) => {
            const k = Number(key);
            if (k < idx) updated[k] = value;
            else if (k > idx) updated[k - 1] = value;
          });
          return updated;
        });
        if (editingIndex === idx) {
          setEditingIndex(null);
          setEditBuffer({});
        } else if (editingIndex > idx) setEditingIndex(editingIndex - 1);
      }
    },
    [data, editingIndex, setData, onAnnotationsChange],
  );

  const colWidths = useMemo(
    () =>
      visibleColumns.map((h) => Math.max(COL_MIN_WIDTH, h.length * 8 + 40)),
    [visibleColumns],
  );
  const totalRowWidth = useMemo(
    () =>
      ROW_NUM_WIDTH + ACTIONS_COL_WIDTH + colWidths.reduce((sum, w) => sum + w, 0),
    [colWidths],
  );
  const totalHeight = filteredData.length * ROW_HEIGHT;
  const hasFilter = Object.entries(filterText).some(
    ([k, v]) => v && !hiddenColumns.has(k),
  );

  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(containerHeight / ROW_HEIGHT) + 2 * OVERSCAN;
  const endIndex = Math.min(filteredData.length, startIndex + visibleCount);

  const visibleRows = [];
  for (let i = startIndex; i < endIndex; i++) {
    const row = filteredData[i];
    const originalIndex = filteredIndexMap.get(i) ?? i;
    const isEditing = editingIndex === originalIndex;
    const isRunningBell = runningBellIdx === originalIndex;
    const rowBellResult = bellResultsMap?.[originalIndex] || null;
    const bellConf = rowBellResult?.confidence;
    const bellConfClass =
      bellConf === "HIGH" ? "high" : bellConf === "MEDIUM" ? "med" : "low";

    visibleRows.push(
      <div
        key={originalIndex}
        className={`virtual-table-row ${isEditing ? "editing" : ""} ${chatActiveIndex === originalIndex ? "chat-active" : ""} ${bellActiveIndex === originalIndex && !isEditing ? "bell-active" : ""}`}
        style={{
          position: "absolute",
          top: i * ROW_HEIGHT,
          height: ROW_HEIGHT,
          width: totalRowWidth,
        }}
      >
        <div
          className="virtual-table-cell virtual-table-rownum"
          style={{ width: ROW_NUM_WIDTH, minWidth: ROW_NUM_WIDTH }}
          title={`Row ${originalIndex + 1} (matches "Row #" in the export)`}
        >
          {originalIndex + 1}
        </div>
        <div
          className="virtual-table-cell virtual-table-cell-actions"
          style={{
            width: ACTIONS_COL_WIDTH,
            minWidth: ACTIONS_COL_WIDTH,
            justifyContent: "center",
          }}
        >
          {isEditing ? (
            <>
              <button className="action-btn save-btn" onClick={handleSave}>
                <Img src={okIcon} />
              </button>
              <button className="action-btn cancel-btn" onClick={handleCancel}>
                <Img src={cancelIcon} />
              </button>
            </>
          ) : (
            <>
              <button
                className="action-btn edit-btn"
                onClick={() => handleEdit(originalIndex)}
                disabled={editingIndex !== null}
                style={
                  editingIndex !== null
                    ? { opacity: 0.4, cursor: "not-allowed" }
                    : {}
                }
              >
                <Img src={editIcon} />
              </button>
              <button
                className="action-btn delete-btn"
                onClick={() => handleDelete(originalIndex)}
                disabled={editingIndex !== null}
                style={
                  editingIndex !== null
                    ? { opacity: 0.4, cursor: "not-allowed" }
                    : {}
                }
              >
                <Img src={deleteIcon} />
              </button>
              <button
                className="action-btn chat-btn"
                onClick={() => onChat && onChat(row, originalIndex)}
                disabled={editingIndex !== null}
                style={
                  editingIndex !== null
                    ? { opacity: 0.4, cursor: "not-allowed" }
                    : {}
                }
              >
                <Img src={chatIcon} />
              </button>
              {/* BELL button */}
              <button
                className={`bell-run-btn${isRunningBell ? " running" : rowBellResult ? " evaluated" : ""}`}
                onClick={() => onBell && onBell(row, originalIndex)}
                disabled={isRunningBell || editingIndex !== null}
                title={
                  rowBellResult
                    ? `Result: ${bellConf} click to re-run or view`
                    : "Run evidence pipeline"
                }
              >
                {isRunningBell ? (
                  <ReactLoading />
                ) : rowBellResult ? (
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span className={`bell-conf-badge ${bellConfClass}`}>
                      {bellConf}
                    </span>
                  </span>
                ) : (
                  <Img src={searchDb} />
                )}
              </button>
            </>
          )}
        </div>
        {visibleColumns.map((col, colIdx) => (
          <div
            key={col}
            className="virtual-table-cell"
            style={{ width: colWidths[colIdx], minWidth: colWidths[colIdx] }}
          >
            {isEditing ? (
              <FieldEditor
                col={col}
                value={editBuffer[col] ?? ""}
                onChange={(v) => setEditBuffer((p) => ({ ...p, [col]: v }))}
                onEnter={handleSave}
                onEscape={handleCancel}
                autoFocus={col === visibleColumns[0]}
              />
            ) : col === "Paper IDs" && row[col] ? (
              <span>
                {String(row[col])
                  .split(",")
                  .map((id, pidx, arr) => {
                    const trimmed = id.trim();
                    const pmidMatch = trimmed.match(/PMID[:\s]*(\d+)/i);
                    return (
                      <div key={pidx}>
                        {pmidMatch ? (
                          <a
                            href={`https://pubmed.ncbi.nlm.nih.gov/${pmidMatch[1]}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              color: "#0066cc",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {trimmed}
                          </a>
                        ) : (
                          trimmed
                        )}
                        {pidx < arr.length - 1 ? ", " : ""}
                      </div>
                    );
                  })}
              </span>
            ) : col === "Statements" && row[col] ? (
              <ExpandableCell
                value={String(row[col])}
                row={row}
                savedData={annotationsMap[originalIndex] || null}
                onSaveResult={(result) => {
                  onAnnotationsChange((prev) => ({
                    ...prev,
                    [originalIndex]: result,
                  }));

                  // Write annotated values into this row's table fields
                  const updates = result.fieldUpdates || {};
                  if (Object.keys(updates).length > 0) {
                    const next = [...data];
                    next[originalIndex] = {
                      ...next[originalIndex],
                      ...updates,
                    };
                    setData(next);
                  }
                }}
              />
            ) : (
              row[col]
            )}
          </div>
        ))}
      </div>,
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="virtual-table-wrapper" style={{ position: "relative" }}>
        <div className="empty-state">
          <div className="empty-icon">🧬</div>
          <div>
            No interactions extracted yet. Search PubMed and extract to see
            results here.
          </div>
        </div>
        {isLoading && (
          <div className="table-loading-overlay">
            <ReactLoading type="spin" color="#003594" height={40} width={40} />
            <span className="table-loading-text">
              Extracting interactions...
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="virtual-table-wrapper" style={{ position: "relative" }}>
      <div className="virtual-table-toolbar">
        <ColumnFilterDropdown
          columns={columns}
          hiddenColumns={hiddenColumns}
          pinnedColumn={pinnedColumn}
          onToggle={toggleColumn}
          onShowAll={showAllColumns}
          onHideAll={hideAllColumns}
        />
        {hasFilter && (
          <button
            onClick={clearFilters}
            style={{
              padding: "3px 10px",
              fontSize: "11px",
              border: "1px solid #C8C9C7",
              borderRadius: "4px",
              background: "#fff",
              cursor: "pointer",
              color: "#00205B",
            }}
          >
            Clear Filters
          </button>
        )}
        <span style={{ fontSize: "12px", color: "#555" }}>
          Showing {filteredData.length} of {data.length} interactions
          {hiddenColumns.size > 0 &&
            ` · ${visibleColumns.length} of ${columns.length} columns shown`}
        </span>
      </div>
      <div className="virtual-table-header-scroll" ref={headerScrollRef}>
        <div
          className="virtual-table-header-row"
          style={{ width: totalRowWidth }}
        >
          <div
            className="virtual-table-header-cell"
            style={{
              width: ROW_NUM_WIDTH,
              minWidth: ROW_NUM_WIDTH,
              backgroundColor: "#ccc",
              color: "#00205B",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <span>#</span>
          </div>
          <div
            className="virtual-table-header-cell"
            style={{
              width: ACTIONS_COL_WIDTH,
              minWidth: ACTIONS_COL_WIDTH,
              textAlign: "center",
              backgroundColor: "#FFB81C",
              color: "#00205B",
              fontSize: "13px",
              justifyContent: "center",
            }}
          >
            <span>Actions</span>
          </div>
          {visibleColumns.map((header, colIdx) => (
            <div
              key={header}
              className="virtual-table-header-cell"
              style={{ width: colWidths[colIdx], minWidth: colWidths[colIdx] }}
            >
              <span>{header}</span>
              <input
                type="text"
                placeholder="Filter..."
                value={filterText[header] || ""}
                onChange={(e) => handleFilterChange(header, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ))}
        </div>
      </div>
      <div
        className="virtual-table-body-scroll"
        ref={bodyScrollRef}
        onScroll={handleBodyScroll}
      >
        <div
          style={{
            position: "relative",
            height: totalHeight,
            width: totalRowWidth,
          }}
        >
          {visibleRows}
        </div>
      </div>
      {isLoading && (
        <div className="table-loading-overlay">
          <ReactLoading type="spin" color="#003594" height={40} width={40} />
          <span className="table-loading-text">Extracting interactions...</span>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// SEARCH SKELETON
// ─────────────────────────────────────────────
const SearchSkeleton = () => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <div className="skeleton-item" key={i}>
        <div className="skeleton-line title" />
        <div className="skeleton-line authors" />
        <div className="skeleton-line meta" />
      </div>
    ))}
  </>
);

// ─────────────────────────────────────────────
// STEP INDICATOR
// ─────────────────────────────────────────────
const StepIndicator = ({ currentStep }) => {
  const steps = [
    { num: 1, label: "Search PubMed" },
    { num: 2, label: "Select Papers" },
    { num: 3, label: "Extract Interactions" },
  ];
  return (
    <div className="step-indicator">
      {steps.map((step, idx) => {
        const status =
          step.num < currentStep
            ? "completed"
            : step.num === currentStep
              ? "active"
              : "upcoming";
        return (
          <React.Fragment key={step.num}>
            {idx > 0 && (
              <div
                className={`step-connector ${step.num <= currentStep ? "completed" : ""}`}
              />
            )}
            <div className={`step-node ${status}`}>
              <div className="step-number">
                {status === "completed" ? "✓" : step.num}
              </div>
              {step.label}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────
// PUBMED RESULT ITEM
// ─────────────────────────────────────────────
const PubMedResultItem = ({ paper, isSelected, onToggle }) => {
  const [showAbstract, setShowAbstract] = useState(false);
  return (
    <div
      className={`result-item ${isSelected ? "selected" : ""}`}
      onClick={onToggle}
    >
      <input
        type="checkbox"
        className="result-checkbox"
        checked={isSelected}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="result-content">
        <div className="result-title">{paper.title}</div>
        <div className="result-authors">{paper.authors}</div>
        <div className="result-meta">
          <span className="result-pmid">
            PMID:{" "}
            <a
              href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#0066cc", textDecoration: "underline" }}
            >
              {paper.pmid}
            </a>
          </span>
          {paper.journal && (
            <span className="result-journal">{paper.journal}</span>
          )}
          {paper.date && <span className="result-date">{paper.date}</span>}
        </div>
        {paper.abstract && (
          <>
            <button
              className="result-abstract-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setShowAbstract(!showAbstract);
              }}
            >
              {showAbstract ? "Hide abstract" : "Show abstract"}
            </button>
            {showAbstract && (
              <div className="result-abstract">{paper.abstract}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MOCK LLAMA
// ─────────────────────────────────────────────
const generateMockLlamaResponse = (interaction, userMessage) => {
  const reg = interaction["Regulator Name"] || "regulator";
  const tgt = interaction["Regulated Name"] || "target";
  const sign = interaction["Sign"] || "unknown";
  const mech = interaction["Mechanism"] || "unknown mechanism";
  const stmt = interaction["Statements"] || "";
  const pmid = interaction["Paper IDs"] || "";
  const tissue = interaction["Tissue Type"] || "";
  const q = userMessage.toLowerCase();

  if (q.includes("mechanism") || q.includes("how"))
    return `The interaction between **${reg}** and **${tgt}** occurs through **${mech}**. ${stmt ? `Specifically, "${stmt}."` : ""} This is a ${sign} regulatory relationship.`;
  if (q.includes("evidence") || q.includes("paper") || q.includes("source"))
    return `This interaction is supported by evidence from ${pmid || "the scientific literature"}. ${stmt ? `The key finding states: "${stmt}."` : ""}`;
  return `Regarding the **${reg} → ${tgt}** interaction:\n\nThis is a **${sign}** regulatory relationship mediated by **${mech}**. ${stmt ? `The literature reports that "${stmt}."` : ""}\n\nWould you like me to elaborate on the mechanism, clinical relevance, or the broader signaling pathway context?`;
};

// ─────────────────────────────────────────────
// CHAT PANEL
// ─────────────────────────────────────────────
const ChatPanel = ({
  interaction,
  onClose,
  onMinimize,
  abstracts,
  rowIndex,
  onSaveHistory,
  fullTextMap,
  onCheckFullText,
  onReextract,
  reextractJob,
  reextractedPmids,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const reg = interaction["Regulator Name"] || "?";
  const tgt = interaction["Regulated Name"] || "?";
  const sign = interaction["Sign"] || "";
  const mech = interaction["Mechanism"] || "";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);
  useEffect(() => {
    if (onSaveHistory && rowIndex != null && messages.length > 0)
      onSaveHistory(rowIndex, messages);
  }, [messages]);
  useEffect(() => {
    setMessages([
      {
        role: "assistant",
        content: `Hi! I'm Kalimba, your biology assistant. I have context about the **${reg} → ${tgt}** interaction (${sign} regulation via ${mech}). Ask me anything about this interaction.\n\nThe first request might take a little extra time. Thanks for your patience!`,
      },
    ]);
  }, [reg, tgt, sign, mech]);

  // ── Re-extraction from the full text (only offered for papers that have it) ──
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const paperPmids = useMemo(
    () => extractPmids(interaction["Paper IDs"]),
    [interaction],
  );
  useEffect(() => {
    if (onCheckFullText && paperPmids.length > 0) onCheckFullText(paperPmids);
  }, [paperPmids, onCheckFullText]);
  const fullTextPmids = paperPmids.filter((p) => fullTextMap && fullTextMap[p]);
  // papers of this interaction that were not re-extracted yet (each paper only once)
  const pendingPmids = fullTextPmids.filter(
    (p) => !(reextractedPmids && reextractedPmids.has(p)),
  );
  const alreadyReextracted =
    fullTextPmids.length > 0 && pendingPmids.length === 0;
  const otherJobRunning = !!reextractJob && !(reextractJob.rowIndex === rowIndex);
  const [reextracting, setReextracting] = useState(false);
  const jobHere =
    reextractJob && reextractJob.rowIndex === rowIndex ? reextractJob : null;
  const progressText = jobHere
    ? jobHere.total > 0
      ? `Reading the paper… ${jobHere.done}/${jobHere.total}`
      : `${jobHere.stage || "Starting"}…`
    : "Starting…";

  const handleReextract = useCallback(async () => {
    if (reextracting || isTyping || !onReextract || pendingPmids.length === 0)
      return;
    const history = messages
      .filter(
        (m) =>
          (m.role === "user" || m.role === "assistant") &&
          !m.content.startsWith("Hi! I'm Kalimba"),
      )
      .map((m) => ({ role: m.role, content: m.content }));
    setReextracting(true);
    setMessages((prev) => [
      ...prev,
      {
        role: "system",
        content: `Re-extracting from the full text of ${pendingPmids.map((p) => `PMID:${p}`).join(", ")}, using this conversation as guidance. This can take several minutes. You can minimize the chat and keep working.`,
      },
    ]);
    try {
      const res = await onReextract(
        interaction,
        rowIndex,
        pendingPmids,
        history,
      );
      if (mountedRef.current) {
        const parts = [
          `${res.added} new interaction${res.added !== 1 ? "s" : ""} added to the table`,
        ];
        if (res.enriched)
          parts.push(
            `${res.enriched} existing row${res.enriched !== 1 ? "s" : ""} completed with missing fields`,
          );
        if (res.duplicates)
          parts.push(`${res.duplicates} already in the table`);
        setMessages((prev) => [
          ...prev,
          {
            role: "system",
            content: `Re-extraction finished: ${parts.join("; ")}.`,
          },
        ]);
      }
    } catch (err) {
      if (mountedRef.current)
        setMessages((prev) => [
          ...prev,
          {
            role: "system",
            content: `Re-extraction failed: ${err.message}`,
          },
        ]);
    } finally {
      if (mountedRef.current) setReextracting(false);
    }
  }, [
    reextracting,
    isTyping,
    onReextract,
    pendingPmids,
    messages,
    interaction,
    rowIndex,
  ]);

  const suggestions = [
    "Explain this interaction within the context of the signaling pathway.",
    "Describe the biological mechanism by which Entity A affects Entity B.",
    "Summarize the evidence provided for this interaction.",
    "Given the sentence and the extracted triple, verify whether the interaction is correctly extracted.",
  ];

  const sendMessage = useCallback(
    async (text) => {
      const msg = (text || input).trim();
      if (!msg || isTyping) return;
      setMessages((prev) => [...prev, { role: "user", content: msg }]);
      setInput("");
      setIsTyping(true);
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 800 + Math.random() * 1500));
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: generateMockLlamaResponse(interaction, msg),
          },
        ]);
        setIsTyping(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/chat/llama/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: msg,
            interaction,
            history: messages.map((m) => ({
              role: m.role,
              content: m.content,
            })),
            abstracts: abstracts || {},
          }),
        });
        if (!res.ok) throw new Error(`Chat failed (${res.status})`);
        const result = await res.json();
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              result.response ||
              result.message ||
              "I couldn't generate a response.",
          },
        ]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            role: "system",
            content: `Error: ${err.message}. Please try again.`,
          },
        ]);
      } finally {
        setIsTyping(false);
      }
    },
    [input, isTyping, interaction, messages, abstracts],
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderContent = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**"))
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      return part.split("\n").map((line, j) => (
        <React.Fragment key={`${i}-${j}`}>
          {j > 0 && <br />}
          {line}
        </React.Fragment>
      ));
    });
  };

  return (
    <>
      <div className="chat-overlay" onClick={onMinimize} />
      <div className="chat-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="chat-drawer-header">
          <span className="chat-drawer-header-icon">🥁</span>
          <span className="chat-drawer-header-title">Chat with LLM</span>
          <button
            className="chat-drawer-minimize"
            onClick={onMinimize}
            title="Minimize"
          >
            −
          </button>
          <button
            className="chat-drawer-close"
            onClick={onClose}
            title="Close chat"
          >
            ×
          </button>
        </div>
        <div className="chat-context-card">
          <div className="chat-context-title">Interaction Context</div>
          <div className="chat-context-interaction">
            <strong>{reg}</strong>
            <span
              style={{
                margin: "0 6px",
                color: sign === "positive" ? "#00AD6E" : "#FF5B45",
              }}
            >
              {sign === "positive" ? "→ +" : sign === "negative" ? "⊣ −" : "→"}
            </span>
            <strong>{tgt}</strong>
          </div>
          <div className="chat-context-detail">
            {[
              mech && `Mechanism: ${mech}`,
              interaction["Connection Type"] &&
                `Type: ${interaction["Connection Type"]}`,
              interaction["Tissue Type"] &&
                `Tissue: ${interaction["Tissue Type"]}`,
              interaction["Paper IDs"] && `Source: ${interaction["Paper IDs"]}`,
            ]
              .filter(Boolean)
              .join("  ·  ")}
          </div>
        </div>
        <div className="chat-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`chat-msg ${msg.role}`}>
              {msg.role === "assistant" && (
                <div className="chat-msg-label">Kalimba</div>
              )}
              {renderContent(msg.content)}
            </div>
          ))}
          {isTyping && (
            <div className="chat-typing">
              <div className="chat-typing-dot" />
              <div className="chat-typing-dot" />
              <div className="chat-typing-dot" />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        {messages.length <= 2 && (
          <div className="chat-suggestions">
            {suggestions.map((s) => (
              <button
                key={s}
                className="chat-suggestion-btn"
                onClick={() => sendMessage(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {pendingPmids.length > 0 && (
          <div className="chat-reextract-bar">
            <button
              className="chat-reextract-btn"
              onClick={handleReextract}
              disabled={reextracting || isTyping || otherJobRunning}
              title={
                otherJobRunning
                  ? "Another re-extraction is still running"
                  : "Read the whole paper again, using this chat as guidance, and add new interactions to the table. Each paper can be re-extracted once."
              }
            >
              {reextracting ? (
                <>
                  <ReactLoading
                    type="spin"
                    color="#003594"
                    height={14}
                    width={14}
                  />
                  {progressText}
                </>
              ) : otherJobRunning ? (
                <>Another re-extraction is running…</>
              ) : (
                <>🔄 Re-extract from full text</>
              )}
            </button>
            <span className="chat-reextract-hint">
              Re-reads the full paper using this chat. New interactions are
              added to the table. Each paper can be re-extracted once.
            </span>
          </div>
        )}
        {alreadyReextracted && (
          <div className="chat-reextract-bar">
            <span className="chat-reextract-hint">
              ✓ Already re-extracted from the full text of{" "}
              {fullTextPmids.map((p) => `PMID:${p}`).join(", ")}. Each paper can
              be re-extracted once.
            </span>
          </div>
        )}
        <div className="chat-input-row">
          <textarea
            ref={inputRef}
            className="chat-input"
            placeholder="Ask Kalimba about this interaction..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            className="chat-send-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || isTyping}
          >
            Send
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// MOCK FLAG
// ─────────────────────────────────────────────
const USE_MOCK = false;

// ─────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────
const MOCK_PUBMED_RESULTS = [
  {
    pmid: "38291045",
    title:
      "EGFR-mediated activation of the PI3K/AKT pathway promotes tumor cell survival in non-small cell lung cancer",
    authors: "Zhang Y, Liu H, Chen W, Wang X, Li J, Patel R",
    journal: "Cancer Research",
    date: "2024 Jan 15",
    abstract:
      "Epidermal growth factor receptor (EGFR) signaling plays a central role in the pathogenesis of non-small cell lung cancer (NSCLC).",
  },
  {
    pmid: "38187623",
    title:
      "TP53 loss-of-function mutations drive resistance to EGFR tyrosine kinase inhibitors through NF-κB activation",
    authors: "Kim S, Park JH, Nakamura T, Davis M, O'Brien C, Fernandez A",
    journal: "Nature Communications",
    date: "2024 Feb 3",
    abstract:
      "Resistance to EGFR tyrosine kinase inhibitors (TKIs) remains a major clinical challenge.",
  },
  {
    pmid: "37994512",
    title: "RAS-RAF-MEK-ERK signaling cascade in KRAS-mutant colorectal cancer",
    authors: "Martinez L, Thompson B, Gupta S, Wilson K, Ahmed F, Romano P",
    journal: "Cell Reports",
    date: "2024 Jan 8",
    abstract:
      "KRAS mutations are found in approximately 40% of colorectal cancers (CRC).",
  },
];

const MOCK_INTERACTIONS_INDRA = [
  {
    Statements:
      "EGFR activates PI3K through direct phosphorylation of the p85 regulatory subunit",
    "Paper IDs": "PMID:38291045",
    "Regulator Name": "EGFR",
    "Regulator Type": "protein",
    "Regulator Subtype": "receptor",
    "Regulator HGNC Symbol": "EGFR",
    "Regulator Database": "HGNC",
    "Regulator ID": "3236",
    "Regulator Compartment": "plasma membrane",
    "Regulator Compartment ID": "GO:0005886",
    "Regulated Name": "PI3K",
    "Regulated Type": "protein",
    "Regulated Subtype": "kinase",
    "Regulated HGNC Symbol": "PIK3CA",
    "Regulated Database": "HGNC",
    "Regulated ID": "8975",
    "Regulated Compartment": "cytoplasm",
    "Regulated Compartment ID": "GO:0005737",
    Sign: "positive",
    "Connection Type": "Activation",
    Mechanism: "phosphorylation",
    Site: "p85 subunit",
    "Cell Line": "HCC827",
    "Cell Type": "epithelial",
    "Tissue Type": "lung",
    Organism: "Homo sapiens",
    Score: "0.95",
    Source: "INDRA/REACH",
  },
  {
    Statements:
      "PI3K activates AKT through PIP3-mediated recruitment and phosphorylation",
    "Paper IDs": "PMID:38291045",
    "Regulator Name": "PI3K",
    "Regulator Type": "protein",
    "Regulator Subtype": "kinase",
    "Regulator HGNC Symbol": "PIK3CA",
    "Regulator Database": "HGNC",
    "Regulator ID": "8975",
    "Regulator Compartment": "cytoplasm",
    "Regulator Compartment ID": "GO:0005737",
    "Regulated Name": "AKT",
    "Regulated Type": "protein",
    "Regulated Subtype": "kinase",
    "Regulated HGNC Symbol": "AKT1",
    "Regulated Database": "HGNC",
    "Regulated ID": "391",
    "Regulated Compartment": "cytoplasm",
    "Regulated Compartment ID": "GO:0005737",
    Sign: "positive",
    "Connection Type": "Activation",
    Mechanism: "phosphorylation",
    Site: "",
    "Cell Line": "HCC827",
    "Cell Type": "epithelial",
    "Tissue Type": "lung",
    Organism: "Homo sapiens",
    Score: "0.92",
    Source: "INDRA/REACH",
  },
  {
    Statements: "TP53 loss leads to constitutive activation of NF-κB signaling",
    "Paper IDs": "PMID:38187623",
    "Regulator Name": "TP53",
    "Regulator Type": "protein",
    "Regulator Subtype": "transcription factor",
    "Regulator HGNC Symbol": "TP53",
    "Regulator Database": "HGNC",
    "Regulator ID": "11998",
    "Regulator Compartment": "nucleus",
    "Regulator Compartment ID": "GO:0005634",
    "Regulated Name": "NFKB1",
    "Regulated Type": "protein",
    "Regulated Subtype": "transcription factor",
    "Regulated HGNC Symbol": "NFKB1",
    "Regulated Database": "HGNC",
    "Regulated ID": "7794",
    "Regulated Compartment": "nucleus",
    "Regulated Compartment ID": "GO:0005634",
    Sign: "negative",
    "Connection Type": "Inhibition",
    Mechanism: "regulation",
    Site: "",
    "Cell Line": "H1975",
    "Cell Type": "epithelial",
    "Tissue Type": "lung",
    Organism: "Homo sapiens",
    Score: "0.90",
    Source: "INDRA/REACH",
  },
];

const MOCK_INTERACTIONS_LLM = MOCK_INTERACTIONS_INDRA.map((row) => ({
  ...row,
  Source: row.Source.replace("INDRA/REACH", "LLM/GPT-4"),
  Score: (parseFloat(row.Score) - 0.05 + Math.random() * 0.1).toFixed(2),
}));

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const KalimbaApp = () => {
  // ── Kalimba state ──
  const [searchQuery, setSearchQuery] = useState("");
  const [maxResults, setMaxResults] = useState(10);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [mode, setMode] = useState(null);
  const [selectedPMIDs, setSelectedPMIDs] = useState(new Set());
  const [extractionMethod, setExtractionMethod] = useState("indra-llm");
  const [methodChangeWarning, setMethodChangeWarning] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractionDone, setExtractionDone] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [chatInteraction, setChatInteraction] = useState(null);
  const [chatRowIndex, setChatRowIndex] = useState(null);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importWarning, setImportWarning] = useState(null);
  const importFileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [beliefThreshold, setBeliefThreshold] = useState(0.7);
  const [annotationsMap, setAnnotationsMap] = useState({});
  const [chatHistories, setChatHistories] = useState({});

  // ── Custom prompt state (NEW) ──
  const [promptText, setPromptText] = useState("");
  const [promptPaperId, setPromptPaperId] = useState("");

  // ── BELL state ──
  const [bellInteraction, setBellInteraction] = useState(null);
  const [bellRowIndex, setBellRowIndex] = useState(null);
  const [bellMinimized, setBellMinimized] = useState(false);
  const [bellResultsMap, setBellResultsMap] = useState({}); // keyed by row index
  const [runningBellIdx, setRunningBellIdx] = useState(null);
  const [bellPipeStep, setBellPipeStep] = useState(0);

  // ── Full-text re-extraction state ──
  const [fullTextMap, setFullTextMap] = useState({}); // pmid -> PMC full text available?
  const [reextractJob, setReextractJob] = useState(null); // { rowIndex, stage, done, total }
  const [reextractNotice, setReextractNotice] = useState(null);
  const [reextractedPmids, setReextractedPmids] = useState(() => new Set());
  const interactionsRef = useRef([]);
  const chatVisibleRef = useRef(false);
  const fullTextAskedRef = useRef(new Set());
  const reextractRunningRef = useRef(false);
  const reextractDoneRef = useRef(new Set());

  // A paper counts as "already re-extracted" if it was re-extracted in this session, or if the
  // table already holds rows produced by a re-extraction of it (e.g. from an imported export).
  const reextractDone = useMemo(() => {
    const done = new Set(reextractedPmids);
    interactions.forEach((r) => {
      if (String(r["Source"] || "").startsWith("LLM (re-extraction)"))
        extractPmids(r["Paper IDs"]).forEach((p) => done.add(p));
    });
    return done;
  }, [reextractedPmids, interactions]);
  useEffect(() => {
    reextractDoneRef.current = reextractDone;
  }, [reextractDone]);
  // a new or emptied table starts with a clean slate
  useEffect(() => {
    if (interactions.length === 0)
      setReextractedPmids((prev) => (prev.size ? new Set() : prev));
  }, [interactions.length]);
  useEffect(() => {
    interactionsRef.current = interactions;
  }, [interactions]);
  useEffect(() => {
    chatVisibleRef.current = !!chatInteraction && !chatMinimized;
  }, [chatInteraction, chatMinimized]);
  useEffect(() => {
    if (reextractNotice) {
      const t = setTimeout(() => setReextractNotice(null), 10000);
      return () => clearTimeout(t);
    }
  }, [reextractNotice]);

  const currentStep = useMemo(() => {
    if (interactions.length > 0 || extractionDone) return 3;
    if (selectedPMIDs.size > 0) return 2;
    return 1;
  }, [interactions.length, extractionDone, selectedPMIDs.size]);

  useEffect(() => {
    if (importWarning) {
      const t = setTimeout(() => setImportWarning(null), 10000);
      return () => clearTimeout(t);
    }
  }, [importWarning]);

  const displayMethod = methodChangeWarning || extractionMethod;

  // ── BELL pipeline runner ──
  const runBellPipeline = useCallback(
    async (row, idx) => {
      setRunningBellIdx(idx);
      setBellPipeStep(0);
      try {
        if (USE_MOCK) {
          for (let s = 0; s < 5; s++) {
            await new Promise((r) => setTimeout(r, 420));
            setBellPipeStep(s + 1);
          }
          const result = mockBellPipeline(row);
          setBellResultsMap((prev) => ({ ...prev, [idx]: result }));
        } else {
          setBellPipeStep(1);
          const norm = await apiNormalize(
            row["Regulator Name"],
            row["Regulated Name"],
          );
          setBellPipeStep(2);
          const routing = await apiRoute({
            regulator: row["Regulator Name"],
            regulated: row["Regulated Name"],
            sign: row["Sign"],
            mechanism: row["Mechanism"],
            cellLine: row["Cell Line"],
            disease: row["Tissue Type"],
          });
          setBellPipeStep(3);
          const topSrc = routing.ranked.map((r) => r.source);
          const qr = await apiQuery(row, topSrc);
          setBellPipeStep(4);
          const sr = await apiScore(qr.evidence, row);
          setBellPipeStep(5);
          const er = await apiExplain(row, qr.evidence, sr);
          const result = {
            normalized: norm,
            grounding: qr.grounding,
            sources: topSrc,
            evidence: qr.evidence,
            scores: { S1: sr.S1, S2: sr.S2, S3: sr.S3, S4: sr.S4 },
            confidence: sr.confidence,
            flags: sr.flags || [],
            contradictions: sr.contradictions || [],
            cot: er.cot,
            action: er.action,
            dataSource: "api",
          };
          setBellResultsMap((prev) => ({ ...prev, [idx]: result }));
        }
      } catch (err) {
        // fallback to mock
        for (let s = bellPipeStep; s < 5; s++) {
          await new Promise((r) => setTimeout(r, 200));
          setBellPipeStep(s + 1);
        }
        const fb = mockBellPipeline(row);
        fb.dataSource = "mock-fallback";
        setBellResultsMap((prev) => ({ ...prev, [idx]: fb }));
      }
      setRunningBellIdx(null);
    },
    [bellPipeStep],
  );

  const handleOpenBell = useCallback((row, idx) => {
    setBellInteraction(row);
    setBellRowIndex(idx);
    setBellMinimized(false);
  }, []);

  const handleRunBellFromDrawer = useCallback(() => {
    if (bellInteraction && bellRowIndex !== null) {
      runBellPipeline(bellInteraction, bellRowIndex);
    }
  }, [bellInteraction, bellRowIndex, runBellPipeline]);

  // ── Export All — single workbook, five sheets ──
  const handleExportAll = useCallback(() => {
    if (!interactions.length) {
      alert("No interactions to export yet.");
      return;
    }

    const wb = XLSX.utils.book_new();

    // ── Sheet 1: Summary ──
    const annotatedCount = Object.keys(annotationsMap).length;
    const bellCount = Object.keys(bellResultsMap).length;
    const chatCount = Object.keys(chatHistories).length;
    const bellHigh = Object.values(bellResultsMap).filter(
      (b) => b.confidence === "HIGH",
    ).length;
    const bellMed = Object.values(bellResultsMap).filter(
      (b) => b.confidence === "MEDIUM",
    ).length;
    const bellLow = Object.values(bellResultsMap).filter(
      (b) => b.confidence === "LOW",
    ).length;

    const summaryRows = [
      {
        Category: "Interactions",
        Metric: "Total interactions",
        Value: interactions.length,
      },
      {
        Category: "Interactions",
        Metric: "Extraction method",
        // CHANGED: custom prompt mode reports its own method
        Value:
          mode === "prompt"
            ? "LLM (custom prompt)"
            : extractionMethod === "indra"
              ? "NLP"
              : extractionMethod === "indra-llm"
                ? "NLP + LLM"
                : "LLM",
      },
      {
        Category: "Annotations",
        Metric: "Annotated rows",
        Value: annotatedCount,
      },
      {
        Category: "Annotations",
        Metric: "Unannotated rows",
        Value: interactions.length - annotatedCount,
      },
      {
        Category: "Verification",
        Metric: "Evaluated rows",
        Value: bellCount,
      },
      {
        Category: "Verification",
        Metric: "HIGH confidence",
        Value: bellHigh,
      },
      {
        Category: "Verification",
        Metric: "MEDIUM confidence",
        Value: bellMed,
      },
      {
        Category: "Verification",
        Metric: "LOW confidence",
        Value: bellLow,
      },
      {
        Category: "Verification",
        Metric: "Not yet evaluated",
        Value: interactions.length - bellCount,
      },
      {
        Category: "Chat History",
        Metric: "Conversations started",
        Value: chatCount,
      },
      {
        Category: "Chat History",
        Metric: "Total messages",
        Value: Object.values(chatHistories).reduce(
          (sum, msgs) => sum + msgs.filter((m) => m.role !== "system").length,
          0,
        ),
      },
      {
        Category: "Export",
        Metric: "Exported at",
        Value: new Date().toLocaleString(),
      },
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summaryRows, {
      header: ["Category", "Metric", "Value"],
    });
    wsSummary["!cols"] = [{ wch: 22 }, { wch: 30 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    // ── Sheet 2: Interactions ──
    const interactionRows = interactions.map((r, i) => {
      const o = { "Row #": i + 1 };
      INTERACTION_COLUMNS.forEach((c) => {
        o[c] = r[c] ?? "";
      });
      return o;
    });
    const wsInteractions = XLSX.utils.json_to_sheet(interactionRows);
    wsInteractions["!cols"] = Object.keys(interactionRows[0] || {}).map(
      (k) => ({ wch: Math.max(k.length + 2, 12) }),
    );
    XLSX.utils.book_append_sheet(wb, wsInteractions, "Interactions");

    // ── Sheet 3: Annotations ──
    const annotationRows = [];
    Object.entries(annotationsMap).forEach(([rowIdx, data]) => {
      const idx = Number(rowIdx);
      const row = interactions[idx];
      if (!row) return;
      const anns = data.annotations || [];
      if (anns.length > 0) {
        anns.forEach((ann) => {
          annotationRows.push({
            "Row #": idx + 1,
            Regulator: row["Regulator Name"] || "",
            Regulated: row["Regulated Name"] || "",
            Statement: row["Statements"] || "",
            "Paper IDs": row["Paper IDs"] || "",
            "Highlighted Text": ann.text || "",
            Label: ann.label || "",
            "Field Value": row[ann.label] || "",
            "Annotation Note": ann.note || "",
            "General Note": data.generalNote || "",
            "Overall Rating": data.evaluation?.overallRating || "",
            "Overall Comment": data.evaluation?.evaluationNote || "",
          });
        });
      } else if (data.generalNote || data.evaluation?.overallRating) {
        annotationRows.push({
          "Row #": idx + 1,
          Regulator: row["Regulator Name"] || "",
          Regulated: row["Regulated Name"] || "",
          Statement: row["Statements"] || "",
          "Paper IDs": row["Paper IDs"] || "",
          "Highlighted Text": "",
          Label: "",
          "Field Value": "",
          "Annotation Note": "",
          "General Note": data.generalNote || "",
          "Overall Rating": data.evaluation?.overallRating || "",
          "Overall Comment": data.evaluation?.evaluationNote || "",
        });
      }
    });
    if (annotationRows.length > 0) {
      const wsAnnotations = XLSX.utils.json_to_sheet(annotationRows);
      wsAnnotations["!cols"] = Object.keys(annotationRows[0]).map((k) => ({
        wch: Math.max(k.length + 2, k === "Statement" ? 50 : 18),
      }));
      XLSX.utils.book_append_sheet(wb, wsAnnotations, "Annotations");
    } else {
      const wsAnnotations = XLSX.utils.json_to_sheet([
        { Note: "No annotations recorded yet." },
      ]);
      XLSX.utils.book_append_sheet(wb, wsAnnotations, "Annotations");
    }

    // ── Sheet 4: BELL Results ──
    const bellRows = Object.entries(bellResultsMap)
      .map(([rowIdx, bell]) => {
        const idx = Number(rowIdx);
        const row = interactions[idx];
        if (!row) return null;
        return {
          "Row #": idx + 1,
          "Regulator Name": row["Regulator Name"] || "",
          "Regulated Name": row["Regulated Name"] || "",
          Sign: row["Sign"] || "",
          Mechanism: row["Mechanism"] || "",
          "Cell Line": row["Cell Line"] || "",
          Organism: row["Organism"] || "",
          "Paper IDs": row["Paper IDs"] || "",
          Statements: row["Statements"] || "",
          Confidence: bell.confidence || "",
          "S1 Source Coverage": bell.scores?.S1 ?? "",
          "S2 Attribute Match": bell.scores?.S2 ?? "",
          "S3 INDRA Belief": bell.scores?.S3 ?? "",
          "S4 Contradiction": bell.scores?.S4 ?? "",
          Flags: (bell.flags || []).join("; "),
          "Sources Queried": (bell.sources || []).join("; "),
          "Recommended Action": bell.action || "",
          "Data Source": bell.dataSource || "",
        };
      })
      .filter(Boolean);
    if (bellRows.length > 0) {
      const wsBell = XLSX.utils.json_to_sheet(bellRows);
      wsBell["!cols"] = Object.keys(bellRows[0]).map((k) => ({
        wch: Math.max(k.length + 2, 14),
      }));
      XLSX.utils.book_append_sheet(wb, wsBell, "Results");
    } else {
      const wsBell = XLSX.utils.json_to_sheet([
        { Note: "No BELL evaluations run yet." },
      ]);
      XLSX.utils.book_append_sheet(wb, wsBell, "Results");
    }

    // ── Sheet 5: Chat History ──
    const chatRows = [];
    Object.entries(chatHistories).forEach(([rowIdx, messages]) => {
      const idx = Number(rowIdx);
      const row = interactions[idx];
      if (!row) return;
      messages.forEach((msg, msgIdx) => {
        if (msg.role === "system") return;
        chatRows.push({
          "Row #": idx + 1,
          "Regulator Name": row["Regulator Name"] || "",
          "Regulated Name": row["Regulated Name"] || "",
          Sign: row["Sign"] || "",
          Mechanism: row["Mechanism"] || "",
          "Paper IDs": row["Paper IDs"] || "",
          Statements: row["Statements"] || "",
          "Message #": msgIdx + 1,
          Role: msg.role === "user" ? "User" : "Kalimba (LLM)",
          Message: msg.content || "",
        });
      });
    });
    if (chatRows.length > 0) {
      const wsChat = XLSX.utils.json_to_sheet(chatRows);
      wsChat["!cols"] = Object.keys(chatRows[0]).map((k) => ({
        wch: Math.max(k.length + 2, k === "Message" ? 60 : 18),
      }));
      XLSX.utils.book_append_sheet(wb, wsChat, "Chat History");
    } else {
      const wsChat = XLSX.utils.json_to_sheet([
        { Note: "No chat history recorded yet." },
      ]);
      XLSX.utils.book_append_sheet(wb, wsChat, "Chat History");
    }

    XLSX.writeFile(
      wb,
      `KALIMBA_Export_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }, [
    interactions,
    annotationsMap,
    bellResultsMap,
    chatHistories,
    extractionMethod,
    mode, // NEW dependency
  ]);

  // ── Search ──
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchPerformed(true);
    setSearchResults([]);
    setSelectedPMIDs(new Set());
    setInteractions([]);
    setAnnotationsMap({});
    setBellResultsMap({});
    setExtractionDone(false);
    setError(null);
    if (USE_MOCK) {
      await new Promise((r) => setTimeout(r, 1200));
      const q = searchQuery.toLowerCase();
      const keywords = q.split(/\s+/).filter(Boolean);
      let filtered = MOCK_PUBMED_RESULTS.filter((p) =>
        keywords.some((kw) =>
          `${p.title} ${p.abstract} ${p.authors}`.toLowerCase().includes(kw),
        ),
      );
      if (filtered.length === 0) filtered = MOCK_PUBMED_RESULTS;
      setSearchResults(filtered.slice(0, maxResults));
      setSearching(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/pubmed/search/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: searchQuery.trim(),
          max_results: maxResults,
        }),
      });
      if (!res.ok) {
        let msg = `Search failed (${res.status})`;
        try {
          const j = await res.json();
          msg = j.detail || j.error || msg;
        } catch {}
        throw new Error(msg);
      }
      const result = await res.json();
      setSearchResults(result.papers || []);
    } catch (err) {
      setError({ title: "Search Error", message: err.message });
    } finally {
      setSearching(false);
    }
  }, [searchQuery, maxResults]);

  const togglePaper = useCallback((pmid) => {
    setSelectedPMIDs((prev) => {
      const next = new Set(prev);
      if (next.has(pmid)) next.delete(pmid);
      else next.add(pmid);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (selectedPMIDs.size === searchResults.length)
      setSelectedPMIDs(new Set());
    else setSelectedPMIDs(new Set(searchResults.map((p) => p.pmid)));
  }, [searchResults, selectedPMIDs.size]);

  const abstractsMap = useMemo(() => {
    const map = {};
    searchResults.forEach((paper) => {
      if (paper.pmid && paper.abstract) map[paper.pmid] = paper.abstract;
    });
    return map;
  }, [searchResults]);

  // ── Extract ──
  const handleExtract = useCallback(async () => {
    if (selectedPMIDs.size === 0) return;
    setExtracting(true);
    setExtractionDone(false);
    setInteractions([]);
    setAnnotationsMap({});
    setBellResultsMap({});
    setChatHistories({});
    setError(null);
    if (USE_MOCK) {
      await new Promise((r) =>
        setTimeout(r, extractionMethod === "llm" ? 2500 : 1800),
      );
      const mockData =
        extractionMethod === "indra-llm"
          ? MOCK_INTERACTIONS_INDRA
          : MOCK_INTERACTIONS_LLM;
      const selectedSet = new Set(
        Array.from(selectedPMIDs).map((id) => `PMID:${id}`),
      );
      let filtered = mockData.filter((row) =>
        (row["Paper IDs"] || "")
          .split(",")
          .map((s) => s.trim())
          .some((pid) => selectedSet.has(pid)),
      );
      if (filtered.length === 0) filtered = mockData;
      setInteractions(filtered);
      setExtractionDone(true);
      setExtracting(false);
      return;
    }
    try {
      const endpoint =
        extractionMethod === "indra"
          ? `${API_BASE}/extract/indra/`
          : extractionMethod === "indra-llm"
            ? `${API_BASE}/extract/indra-llm/`
            : `${API_BASE}/extract/llm/`;
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pmids: Array.from(selectedPMIDs),
          method: extractionMethod,
          belief_threshold: beliefThreshold,
          abstracts: abstractsMap,
        }),
      });
      if (!res.ok) {
        let msg = `Extraction failed (${res.status})`;
        try {
          const j = await res.json();
          msg = j.detail || j.error || msg;
        } catch {}
        throw new Error(msg);
      }
      const result = await res.json();
      const rows = result.interactions || result.dataframe || result.data || [];
      if (!Array.isArray(rows) || rows.length === 0)
        throw new Error(
          "No interactions were extracted from the selected papers.",
        );
      const clean = rows.map((r) => {
        const o = {};
        INTERACTION_COLUMNS.forEach((c) => {
          o[c] = r[c] != null ? String(r[c]) : "";
        });
        return o;
      });
      setInteractions(clean);
      setExtractionDone(true);
    } catch (err) {
      setError({
        title: err.name === "TypeError" ? "Network Error" : "Extraction Error",
        message: err.message,
      });
    } finally {
      setExtracting(false);
    }
  }, [selectedPMIDs, extractionMethod, beliefThreshold, abstractsMap]);

  // ── Extract from custom prompt (NEW) ──
  const handleExtractFromPrompt = useCallback(async () => {
    if (!promptText.trim()) return;
    setExtracting(true);
    setExtractionDone(false);
    setInteractions([]);
    setAnnotationsMap({});
    setBellResultsMap({});
    setChatHistories({});
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/extract/text/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: promptText,
          paper_id: promptPaperId.trim(),
        }),
      });
      if (!res.ok) {
        let msg = `Extraction failed (${res.status})`;
        try {
          const j = await res.json();
          msg = j.detail || j.error || msg;
        } catch {}
        throw new Error(msg);
      }
      const result = await res.json();
      const rows = result.interactions || [];
      if (!Array.isArray(rows) || rows.length === 0)
        throw new Error("No interactions were extracted from the prompt.");
      const clean = rows.map((r) => {
        const o = {};
        INTERACTION_COLUMNS.forEach((c) => {
          o[c] = r[c] != null ? String(r[c]) : "";
        });
        return o;
      });
      setInteractions(clean);
      setExtractionDone(true);
    } catch (err) {
      setError({
        title: err.name === "TypeError" ? "Network Error" : "Extraction Error",
        message: err.message,
      });
    } finally {
      setExtracting(false);
    }
  }, [promptText, promptPaperId]);

  // ── Import ──
  const processImportFile = useCallback((file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (![".xlsx", ".xls", ".csv", ".tsv"].includes(ext)) {
      setError({
        title: "Invalid file type",
        message: `Please upload .xlsx, .xls, .csv, or .tsv.`,
      });
      return;
    }
    setImportLoading(true);
    setImportWarning(null);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let json;
        if (ext === ".csv" || ext === ".tsv") {
          const text = new TextDecoder().decode(
            new Uint8Array(e.target.result),
          );
          const wb = XLSX.read(text, {
            type: "string",
            FS: ext === ".tsv" ? "\t" : ",",
          });
          json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
            defval: "",
          });
        } else {
          const wb = XLSX.read(e.target.result, { type: "array" });
          json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
            defval: "",
          });
        }
        if (!json?.length) {
          setError({ title: "Empty file", message: "No data rows found." });
          setImportLoading(false);
          return;
        }
        const fileCols = Object.keys(json[0]);
        const unexpected = fileCols.filter(
          (c) => !INTERACTION_COLUMNS.includes(c),
        );
        if (
          unexpected.length > 0 &&
          unexpected.length > fileCols.length * 0.5
        ) {
          setError({
            title: "Unrecognized format",
            message:
              "The file doesn't appear to be a BioRECIPE interaction list.",
          });
          setImportLoading(false);
          return;
        }
        const missing = INTERACTION_COLUMNS.filter(
          (c) => !fileCols.includes(c),
        );
        if (missing.length > 0)
          setImportWarning({
            title: "Missing columns",
            message: "Some expected BioRECIPE-IL columns were not found.",
            missing,
          });
        const clean = json.map((r) => {
          const o = {};
          INTERACTION_COLUMNS.forEach((c) => {
            o[c] = r[c] !== undefined ? String(r[c]) : "";
          });
          return o;
        });
        setInteractions(clean);
        setReextractedPmids(new Set());
        setAnnotationsMap({});
        setBellResultsMap({});
        setChatHistories({});
        setExtractionDone(true);
      } catch (err) {
        setError({ title: "Read error", message: err.message });
      } finally {
        setImportLoading(false);
      }
    };
    reader.onerror = () => {
      setError({ title: "Read error", message: "Failed to read the file." });
      setImportLoading(false);
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleImportClick = useCallback(() => {
    if (importFileRef.current) {
      importFileRef.current.value = "";
      importFileRef.current.click();
    }
  }, []);
  const handleImportFile = useCallback(
    (e) => {
      processImportFile(e.target.files[0]);
    },
    [processImportFile],
  );
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) processImportFile(file);
    },
    [processImportFile],
  );
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleStartOver = useCallback(() => {
    setMode(null);
    setSearchQuery("");
    setSearchResults([]);
    setSearchPerformed(false);
    setSelectedPMIDs(new Set());
    setInteractions([]);
    setExtractionDone(false);
    setExtracting(false);
    setChatInteraction(null);
    setChatRowIndex(null);
    setChatMinimized(false);
    setError(null);
    setImportWarning(null);
    setMethodChangeWarning(null);
    setAnnotationsMap({});
    setChatHistories({});
    setBellInteraction(null);
    setBellRowIndex(null);
    setBellMinimized(false);
    setBellResultsMap({});
    setRunningBellIdx(null);
    setPromptText(""); // NEW
    setPromptPaperId(""); // NEW
  }, []);

  const handleMethodChange = useCallback(
    (newMethod) => {
      if (newMethod === extractionMethod) return;
      if (interactions.length > 0 || extractionDone)
        setMethodChangeWarning(newMethod);
      else setExtractionMethod(newMethod);
    },
    [extractionMethod, interactions.length, extractionDone],
  );

  const confirmMethodChange = useCallback(() => {
    if (methodChangeWarning) {
      setExtractionMethod(methodChangeWarning);
      setInteractions([]);
      setAnnotationsMap({});
      setBellResultsMap({});
      setExtractionDone(false);
      setMethodChangeWarning(null);
    }
  }, [methodChangeWarning]);

  const cancelMethodChange = useCallback(
    () => setMethodChangeWarning(null),
    [],
  );

  // ── Which papers have PMC full text? (asked once per PMID) ──
  const checkFullText = useCallback(async (pmids) => {
    const unknown = pmids.filter((p) => !fullTextAskedRef.current.has(p));
    if (unknown.length === 0) return;
    unknown.forEach((p) => fullTextAskedRef.current.add(p));
    try {
      const { availability } = await apiFullTextStatus(unknown);
      setFullTextMap((prev) => ({ ...prev, ...availability }));
      // a PMID the server could not check is asked again the next time a chat opens
      unknown.forEach((p) => {
        if (!(p in availability)) fullTextAskedRef.current.delete(p);
      });
    } catch {
      // let the next chat open try again; without an answer there is simply no button
      unknown.forEach((p) => fullTextAskedRef.current.delete(p));
    }
  }, []);

  // ── Re-extract from the full text, guided by the chat; add the results to the table ──
  const handleReextract = useCallback(
    async (interaction, rowIdx, pmids, history) => {
      // each paper can be re-extracted only once
      const pending = pmids.filter((p) => !reextractDoneRef.current.has(p));
      if (pending.length === 0)
        throw new Error("This paper has already been re-extracted from its full text.");
      if (reextractRunningRef.current)
        throw new Error("Another re-extraction is still running.");
      reextractRunningRef.current = true;
      setReextractJob({ rowIndex: rowIdx, stage: "starting", done: 0, total: 0 });
      try {
        const { job_id } = await apiReextractStart(pending, interaction, history);
        let status;
        for (;;) {
          await new Promise((r) => setTimeout(r, 2000));
          status = await apiReextractStatus(job_id);
          const p = status.progress || {};
          setReextractJob({
            rowIndex: rowIdx,
            stage: p.stage || "",
            done: p.done || 0,
            total: p.total || 0,
          });
          if (status.status === "done") break;
          if (status.status === "error")
            throw new Error(status.error || "Re-extraction failed.");
        }
        // the re-extraction ran to the end: this paper cannot be re-extracted again
        setReextractedPmids((prev) => new Set([...prev, ...pending]));
        const incoming = (status.interactions || []).map((r) => {
          const o = {};
          INTERACTION_COLUMNS.forEach((c) => {
            o[c] = r[c] != null ? String(r[c]) : "";
          });
          return o;
        });
        const { added, enrich, duplicates } = mergeReextractedRows(
          interactionsRef.current,
          incoming,
        );
        if (added.length > 0 || Object.keys(enrich).length > 0) {
          setInteractions((prev) => {
            const m = mergeReextractedRows(prev, incoming);
            return [
              ...prev.map((r, i) => (m.enrich[i] ? { ...r, ...m.enrich[i] } : r)),
              ...m.added,
            ];
          });
          setExtractionDone(true);
        }
        const result = {
          found: incoming.length,
          added: added.length,
          enriched: Object.keys(enrich).length,
          duplicates,
        };
        if (!chatVisibleRef.current) {
          setReextractNotice({
            title: "Re-extraction finished",
            message: `${result.added} new interaction${result.added !== 1 ? "s" : ""} added to the table${result.enriched ? `, ${result.enriched} existing row${result.enriched !== 1 ? "s" : ""} completed` : ""}.`,
          });
        }
        return result;
      } finally {
        reextractRunningRef.current = false;
        setReextractJob(null);
      }
    },
    [],
  );

  // ── Derived counts ──
  const bellEvaluatedCount = Object.keys(bellResultsMap).length;

  return (
    <>
      <style>{styles + fieldEditorStyles + reextractStyles + columnFilterStyles}</style>

      {error && (
        <div className="error-banner">
          <div className="error-banner-content">
            <div className="error-banner-header">
              <span>❌ {error.title}</span>
              <button
                className="error-banner-close"
                onClick={() => setError(null)}
              >
                ×
              </button>
            </div>
            <div className="error-banner-message">{error.message}</div>
          </div>
        </div>
      )}

      {importWarning && !error && (
        <div className="warning-banner">
          <div className="warning-banner-content">
            <div className="warning-banner-header">
              <span>⚠️ {importWarning.title}</span>
              <button
                className="warning-banner-close"
                onClick={() => setImportWarning(null)}
              >
                ×
              </button>
            </div>
            <div className="warning-banner-message">
              {importWarning.message}
            </div>
            {importWarning.missing && (
              <div className="warning-columns">
                <div className="warning-columns-label">
                  Missing columns (added as empty):
                </div>
                <div className="warning-columns-list">
                  {importWarning.missing.join(", ")}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {reextractNotice && (
        <div className="info-banner">
          <div className="info-banner-header">
            <span>✅ {reextractNotice.title}</span>
            <button
              className="info-banner-close"
              onClick={() => setReextractNotice(null)}
            >
              ×
            </button>
          </div>
          <div className="info-banner-message">{reextractNotice.message}</div>
        </div>
      )}

      <input
        type="file"
        ref={importFileRef}
        className="import-file-input"
        accept=".xlsx,.xls,.csv,.tsv"
        onChange={handleImportFile}
      />

      <div className="page-wrapper">
        <div className="body-layout">
          {/* ── SIDEBAR ── */}
          <nav className="sidebar">
            <div className="sidebar-section">
              <div className="sidebar-section-title">Workflow</div>
              <button
                className={`sidebar-item ${mode === "search" ? "active" : ""}`}
                onClick={() => {
                  if (mode !== "search") {
                    handleStartOver();
                    setMode("search");
                  }
                }}
              >
                <span>🔍</span> <span>Search PubMed</span>
              </button>
              <button
                className={`sidebar-item ${mode === "import" ? "active" : ""}`}
                onClick={() => {
                  if (mode !== "import") {
                    handleStartOver();
                    setMode("import");
                  }
                }}
              >
                <span>📂</span> <span>Import Data</span>
              </button>
              {/* NEW: Custom Prompt */}
              <button
                className={`sidebar-item ${mode === "prompt" ? "active" : ""}`}
                onClick={() => {
                  if (mode !== "prompt") {
                    handleStartOver();
                    setMode("prompt");
                  }
                }}
              >
                <span>✍️</span> <span>Custom Prompt</span>
              </button>
            </div>

            <div className="sidebar-footer">
              <div className="sidebar-footer-text">
                {/* <Link to="/about" state={{ fromTool: "pubmed-extractor" }} rel="noopener noreferrer" style={{ color: "#FFB81C", textDecoration: "none", margin: "0 4px" }}>About</Link> */}

                <Link
                  to="/biorecipe"
                  rel="noopener noreferrer"
                  style={{
                    color: "#FFB81C",
                    textDecoration: "none",
                    margin: "0 4px",
                  }}
                >
                  BioRECIPE
                </Link>
                {" · "}
                <Link
                  to="https://pubmed.ncbi.nlm.nih.gov/"
                  rel="noopener noreferrer"
                  style={{
                    color: "#FFB81C",
                    textDecoration: "none",
                    margin: "0 4px",
                  }}
                  target="_blank"
                >
                  PubMed
                </Link>
                {" · "}
                <Link
                  to="https://indra.readthedocs.io/"
                  rel="noopener noreferrer"
                  style={{
                    color: "#FFB81C",
                    textDecoration: "none",
                    margin: "0 4px",
                  }}
                  target="_blank"
                >
                  INDRA
                </Link>
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "10px",
                    color: "rgba(219,238,255,0.7)",
                  }}
                >
                  © 2026{" "}
                  <a
                    href="https://www.nmzlab.pitt.edu/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "rgba(219,238,255,0.7)", margin: "0 4px" }}
                  >
                    Melody Lab.
                  </a>{" "}
                  All rights reserved.
                </div>
              </div>
            </div>
          </nav>

          <main className="main-area">
            {/* ── MODE CHOOSER ── */}
            {mode === null && (
              <div className="mode-chooser">
                <div className="mode-chooser-main-title">KALIMBA</div>
                <div className="mode-chooser-title">
                  Knowledge-Assisted Literature Mining for Biological
                  Interaction Analysis
                </div>
                <div className="mode-chooser-subtitle">
                  let KALIMBA search PubMed or analyze your imported data to
                  extract and verify biological interaction.
                </div>
                <div className="mode-chooser-cards">
                  <div className="mode-card" onClick={() => setMode("search")}>
                    {/* <div className="mode-card-icon">🔍</div> */}
                    <Img src={searchIcone} width="60px" />
                    <div className="mode-card-title">Search PubMed</div>
                    <div className="mode-card-description">
                      Search PubMed and select papers. Then pick your extraction
                      method: LLM, NLP or NLP + LLM (Hybrid).
                    </div>
                    <div className="mode-card-badge search">
                      Full Extraction Pipeline
                    </div>
                  </div>
                  <div className="mode-card" onClick={() => setMode("import")}>
                    <Img src={importIcon} width="60px" />
                    <div className="mode-card-title">Import Data</div>
                    <div className="mode-card-description">
                      Already have a BioRECIPE interaction list? Import it
                      directly to view, edit, chat with LLM, and run BELL
                      verification.
                    </div>
                    <div className="mode-card-badge import">Quick Import</div>
                  </div>
                  {/* NEW: Custom Prompt card */}
                  <div className="mode-card" onClick={() => setMode("prompt")}>
                    <div className="mode-card-icon">✍️</div>
                    <div className="mode-card-title">Custom Prompt</div>
                    <div className="mode-card-description">
                      Paste your own prompt with a paragraph and get the
                      extracted interactions as a BioRECIPE table.
                    </div>
                    <div className="mode-card-badge search">
                      Quick Extraction
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── SEARCH MODE ── */}
            {mode === "search" && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <div>
                    <div className="page-heading">
                      PubMed Search &amp; Interaction Extraction
                    </div>
                    <div className="page-subheading">
                      Search PubMed for papers, select the ones you want, then
                      extract biological interactions.
                    </div>
                  </div>
                </div>

                <StepIndicator currentStep={currentStep} />

                <div className="search-panel">
                  <div className="search-panel-title">🔍 Search PubMed</div>
                  <div className="search-input-row">
                    <input
                      className="search-input"
                      type="text"
                      placeholder="Enter search query (e.g., 'EGFR signaling in lung cancer', 'p53 apoptosis pathway')"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                    />
                    <button
                      className="search-btn"
                      onClick={handleSearch}
                      disabled={searching || !searchQuery.trim()}
                    >
                      {searching ? (
                        <>
                          <ReactLoading
                            type="spin"
                            color="#fff"
                            height={16}
                            width={16}
                          />
                          Searching...
                        </>
                      ) : (
                        <>Search</>
                      )}
                    </button>
                  </div>
                  <div className="search-options">
                    <label className="search-option-label">
                      Max results:
                      <select
                        value={maxResults}
                        onChange={(e) => setMaxResults(Number(e.target.value))}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>
                  </div>
                </div>

                {(searching ||
                  searchResults.length > 0 ||
                  (searchPerformed && !searching)) && (
                  <div className="results-panel">
                    <div className="results-header">
                      <div className="results-header-title">
                        Search Results
                        {searchResults.length > 0 && (
                          <span className="results-count">
                            — {searchResults.length} paper
                            {searchResults.length !== 1 ? "s" : ""} found
                            {selectedPMIDs.size > 0 &&
                              `, ${selectedPMIDs.size} selected`}
                          </span>
                        )}
                      </div>
                      {searchResults.length > 0 && (
                        <div className="results-header-actions">
                          <button
                            className="select-all-btn"
                            onClick={selectAll}
                          >
                            {selectedPMIDs.size === searchResults.length
                              ? "Deselect All"
                              : "Select All"}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="results-list">
                      {searching ? (
                        <SearchSkeleton />
                      ) : searchResults.length === 0 ? (
                        <div
                          className="empty-state"
                          style={{ padding: "32px 0" }}
                        >
                          <div className="empty-icon">🔍</div>
                          <div>No results found. Try a different query.</div>
                        </div>
                      ) : (
                        searchResults.map((paper) => (
                          <PubMedResultItem
                            key={paper.pmid}
                            paper={paper}
                            isSelected={selectedPMIDs.has(paper.pmid)}
                            onToggle={() => togglePaper(paper.pmid)}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}

                {selectedPMIDs.size > 0 && (
                  <div className="extraction-panel">
                    <div className="extraction-panel-title">
                      Choose Extraction Method
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#003594",
                        marginTop: "-4px",
                      }}
                    >
                      Select how you want to extract biological interactions
                      from {selectedPMIDs.size} paper
                      {selectedPMIDs.size !== 1 ? "s" : ""}.
                    </div>
                    <div className="extraction-options">
                      <div
                        className={`extraction-method-card ${displayMethod === "indra" ? "selected" : ""}`}
                        onClick={() => handleMethodChange("indra")}
                      >
                        <div className="method-header">
                          <div className="method-radio" />
                          <div className="method-name">NLP</div>
                        </div>
                        <div className="method-description">
                          Fast NLP-based extraction from pre-built database.
                        </div>
                        <div className="method-badge indra">NLP Pipeline</div>
                      </div>
                      <div
                        className={`extraction-method-card ${displayMethod === "indra-llm" ? "selected" : ""}`}
                        onClick={() => handleMethodChange("indra-llm")}
                      >
                        <div className="method-header">
                          <div className="method-radio" />
                          <div className="method-name">NLP + LLM</div>
                        </div>
                        <div className="method-description">
                          Hybrid pipeline — NLP extracts first, then LLM fills
                          in gaps.
                        </div>
                        <div className="method-badge indra-llm">
                          Hybrid Pipeline
                        </div>
                        {displayMethod === "indra-llm" && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{ marginTop: "10px", marginLeft: "28px" }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#00205B",
                                marginBottom: "6px",
                              }}
                            >
                              Belief Score Threshold:{" "}
                              <span style={{ color: "#003594" }}>
                                {beliefThreshold.toFixed(2)}
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={beliefThreshold}
                              onChange={(e) =>
                                setBeliefThreshold(parseFloat(e.target.value))
                              }
                              style={{ width: "100%", accentColor: "#FFB81C" }}
                            />
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "10px",
                                color: "#888",
                                marginTop: "2px",
                              }}
                            >
                              <span>0 (Keep all NLP)</span>
                              <span>1 (Re-extract all with LLM)</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div
                        className={`extraction-method-card ${displayMethod === "llm" ? "selected" : ""}`}
                        onClick={() => handleMethodChange("llm")}
                      >
                        <div className="method-header">
                          <div className="method-radio" />
                          <div className="method-name">LLM</div>
                        </div>
                        <div className="method-description">
                          Deep extraction via LLM from abstracts and full text.
                        </div>
                        <div className="method-badge llm">LLM Pipeline</div>
                      </div>
                    </div>
                    <button
                      className="extract-btn"
                      onClick={handleExtract}
                      disabled={extracting || selectedPMIDs.size === 0}
                    >
                      {extracting ? (
                        <>
                          <ReactLoading
                            type="spin"
                            color="#00205B"
                            height={18}
                            width={18}
                          />
                          Extracting from {selectedPMIDs.size} paper
                          {selectedPMIDs.size !== 1 ? "s" : ""}...
                        </>
                      ) : (
                        <>
                          Extract Interactions ({selectedPMIDs.size} paper
                          {selectedPMIDs.size !== 1 ? "s" : ""})
                        </>
                      )}
                    </button>
                  </div>
                )}

                {(interactions.length > 0 || extracting) && (
                  <>
                    <div>
                      <div
                        className="page-heading"
                        style={{ fontSize: "18px" }}
                      >
                        Extracted Interactions
                      </div>
                      <div className="page-subheading">
                        {`${interactions.length} interaction${interactions.length !== 1 ? "s" : ""} extracted using ${extractionMethod === "indra" ? "NLP" : extractionMethod === "indra-llm" ? "NLP + LLM" : "LLM"}.`}
                      </div>
                    </div>
                    <div className="table-info">
                      <button
                        className="download-btn"
                        onClick={handleExportAll}
                        disabled={!interactions.length}
                        style={{ background: "#00205B", padding: "10px 28px" }}
                      >
                        <Img src={export_logo} /> Export All
                      </button>
                      <span
                        style={{ fontSize: 11, color: "#888", marginLeft: 4 }}
                      >
                        Interactions · Annotations (
                        {Object.keys(annotationsMap).length}) · Evidence (
                        {bellEvaluatedCount}) · Chat (
                        {Object.keys(chatHistories).length})
                      </span>
                    </div>
                    <InteractionTable
                      data={interactions}
                      setData={setInteractions}
                      columns={INTERACTION_COLUMNS}
                      isLoading={extracting}
                      chatActiveIndex={chatRowIndex}
                      onChat={(row, idx) => {
                        setChatInteraction(row);
                        setChatRowIndex(idx);
                        setChatMinimized(false);
                      }}
                      annotationsMap={annotationsMap}
                      onAnnotationsChange={setAnnotationsMap}
                      onBell={handleOpenBell}
                      bellActiveIndex={bellRowIndex}
                      bellResultsMap={bellResultsMap}
                      runningBellIdx={runningBellIdx}
                    />
                  </>
                )}
              </>
            )}

            {/* ── IMPORT MODE ── */}
            {mode === "import" && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <div>
                    <div className="page-heading">Import Interaction Data</div>
                    <div className="page-subheading">
                      Import an existing BioRECIPE interaction list to view,
                      edit, chat and verify with Kalimba.
                    </div>
                  </div>
                </div>

                <div className="import-panel">
                  <div className="import-panel-title">
                    {interactions.length > 0
                      ? "Replace Data — Import Another File"
                      : "Upload Your Interaction File"}
                  </div>
                  <div className="import-panel-hint">
                    Upload a BioRECIPE-IL formatted file.
                  </div>
                  <div
                    className={`import-dropzone ${dragOver ? "drag-over" : ""}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={handleImportClick}
                  >
                    {importLoading ? (
                      <>
                        <ReactLoading
                          type="spin"
                          color="#003594"
                          height={32}
                          width={32}
                        />
                        <div className="import-dropzone-text">Importing...</div>
                      </>
                    ) : (
                      <>
                        <div className="import-dropzone-icon">📂</div>
                        <div className="import-dropzone-text">
                          Drag &amp; drop a file here, or{" "}
                          <strong>click to browse</strong>
                        </div>
                        <div className="import-dropzone-formats">
                          Supports: .xlsx, .xls, .csv, .tsv (BioRECIPE-IL
                          format)
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {(interactions.length > 0 || importLoading) && (
                  <>
                    <div>
                      <div
                        className="page-heading"
                        style={{ fontSize: "18px" }}
                      >
                        Imported Interactions
                      </div>
                      <div className="page-subheading">
                        {interactions.length > 0
                          ? `${interactions.length} interaction${interactions.length !== 1 ? "s" : ""} loaded from file.`
                          : "Importing..."}
                      </div>
                    </div>
                    <div className="table-info">
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",

                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <button
                          className="import-btn"
                          onClick={handleImportClick}
                          disabled={importLoading}
                        >
                          {importLoading ? (
                            <>
                              <ReactLoading
                                type="spin"
                                color="#00205B"
                                height={18}
                                width={18}
                              />
                              Importing...
                            </>
                          ) : (
                            <>
                              <Img src={import_logo} /> Replace Data
                            </>
                          )}
                        </button>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          <button
                            className="download-btn"
                            onClick={handleExportAll}
                            disabled={!interactions.length}
                            style={{
                              background: "#00205B",
                              padding: "10px 28px",
                            }}
                          >
                            <Img src={export_logo} /> Export All
                          </button>
                          <span style={{ fontSize: 11, color: "#888" }}>
                            Interactions · Annotations (
                            {Object.keys(annotationsMap).length}) · Evidence (
                            {bellEvaluatedCount}) · Chat (
                            {Object.keys(chatHistories).length})
                          </span>
                        </div>
                      </div>
                    </div>
                    <InteractionTable
                      data={interactions}
                      setData={setInteractions}
                      columns={INTERACTION_COLUMNS}
                      isLoading={importLoading}
                      chatActiveIndex={chatRowIndex}
                      onChat={(row, idx) => {
                        setChatInteraction(row);
                        setChatRowIndex(idx);
                        setChatMinimized(false);
                      }}
                      annotationsMap={annotationsMap}
                      onAnnotationsChange={setAnnotationsMap}
                      onBell={handleOpenBell}
                      bellActiveIndex={bellRowIndex}
                      bellResultsMap={bellResultsMap}
                      runningBellIdx={runningBellIdx}
                    />
                  </>
                )}
              </>
            )}

            {/* ── CUSTOM PROMPT MODE (NEW) ── */}
            {mode === "prompt" && (
              <>
                <div>
                  <div className="page-heading">Custom Prompt Extraction</div>
                  <div className="page-subheading">
                    Paste your prompt (including the paragraph) and get the
                    interactions as a BioRECIPE table.
                  </div>
                </div>

                <div className="search-panel">
                  <div className="search-panel-title">✍️ Your Prompt</div>
                  <textarea
                    className="search-input"
                    style={{
                      minHeight: 200,
                      resize: "vertical",
                      fontFamily: "inherit",
                      lineHeight: 1.5,
                    }}
                    placeholder="Paste your prompt here, e.g. 'Extract all regulator-regulated interactions from the following paragraph: ...'"
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                  />
                  <div className="search-options">
                    <label className="search-option-label">
                      Paper ID (optional):
                      <input
                        type="text"
                        placeholder="e.g. 12345678"
                        value={promptPaperId}
                        onChange={(e) => setPromptPaperId(e.target.value)}
                      />
                    </label>
                  </div>
                  <button
                    className="extract-btn"
                    onClick={handleExtractFromPrompt}
                    disabled={extracting || !promptText.trim()}
                  >
                    {extracting ? (
                      <>
                        <ReactLoading
                          type="spin"
                          color="#00205B"
                          height={18}
                          width={18}
                        />
                        Extracting...
                      </>
                    ) : (
                      <>Extract Interactions</>
                    )}
                  </button>
                </div>

                {(interactions.length > 0 || extracting) && (
                  <>
                    <div>
                      <div
                        className="page-heading"
                        style={{ fontSize: "18px" }}
                      >
                        Extracted Interactions
                      </div>
                      <div className="page-subheading">
                        {`${interactions.length} interaction${interactions.length !== 1 ? "s" : ""} extracted from your prompt.`}
                      </div>
                    </div>
                    <div className="table-info">
                      <button
                        className="download-btn"
                        onClick={handleExportAll}
                        disabled={!interactions.length}
                        style={{ background: "#00205B", padding: "10px 28px" }}
                      >
                        <Img src={export_logo} /> Export All
                      </button>
                      <span
                        style={{ fontSize: 11, color: "#888", marginLeft: 4 }}
                      >
                        Interactions · Annotations (
                        {Object.keys(annotationsMap).length}) · Evidence (
                        {bellEvaluatedCount}) · Chat (
                        {Object.keys(chatHistories).length})
                      </span>
                    </div>
                    <InteractionTable
                      data={interactions}
                      setData={setInteractions}
                      columns={INTERACTION_COLUMNS}
                      isLoading={extracting}
                      chatActiveIndex={chatRowIndex}
                      onChat={(row, idx) => {
                        setChatInteraction(row);
                        setChatRowIndex(idx);
                        setChatMinimized(false);
                      }}
                      annotationsMap={annotationsMap}
                      onAnnotationsChange={setAnnotationsMap}
                      onBell={handleOpenBell}
                      bellActiveIndex={bellRowIndex}
                      bellResultsMap={bellResultsMap}
                      runningBellIdx={runningBellIdx}
                    />
                  </>
                )}
              </>
            )}

            {/* ── CHAT PANEL ── */}
            {chatInteraction && (
              <div style={{ display: chatMinimized ? "none" : "contents" }}>
                <ChatPanel
                  interaction={chatInteraction}
                  onMinimize={() => setChatMinimized(true)}
                  abstracts={abstractsMap}
                  rowIndex={chatRowIndex}
                  fullTextMap={fullTextMap}
                  onCheckFullText={checkFullText}
                  onReextract={handleReextract}
                  reextractJob={reextractJob}
                  reextractedPmids={reextractDone}
                  onSaveHistory={(idx, msgs) =>
                    setChatHistories((prev) => ({ ...prev, [idx]: msgs }))
                  }
                  onClose={() => {
                    setChatInteraction(null);
                    setChatRowIndex(null);
                    setChatMinimized(false);
                  }}
                />
              </div>
            )}
            {chatInteraction && chatMinimized && (
              <div
                className="chat-minimized-pill"
                onClick={() => setChatMinimized(false)}
              >
                <span>🥁</span>
                <span className="chat-minimized-pill-text">
                  Chat: {chatInteraction["Regulator Name"] || "?"} →{" "}
                  {chatInteraction["Regulated Name"] || "?"}
                </span>
                <button
                  className="chat-minimized-pill-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    setChatInteraction(null);
                    setChatRowIndex(null);
                    setChatMinimized(false);
                  }}
                >
                  ×
                </button>
              </div>
            )}

            {/* ── BELL DRAWER ── */}
            {bellInteraction && !bellMinimized && (
              <BellDrawer
                interaction={bellInteraction}
                bellResult={bellResultsMap[bellRowIndex] || null}
                isRunning={runningBellIdx === bellRowIndex}
                pipeStep={bellPipeStep}
                onRun={handleRunBellFromDrawer}
                onMinimize={() => setBellMinimized(true)}
                onClose={() => {
                  setBellInteraction(null);
                  setBellRowIndex(null);
                  setBellMinimized(false);
                }}
              />
            )}
            {bellInteraction && bellMinimized && (
              <div
                className="bell-minimized-pill"
                onClick={() => setBellMinimized(false)}
                title="Click to restore BELL"
                style={
                  chatInteraction && chatMinimized
                    ? { top: "calc(50% + 90px)" }
                    : { top: "50%", transform: "translateY(-50%)" }
                }
              >
                <Img src={rarrow} />
                <span className="bell-minimized-pill-text">
                  {bellInteraction["Regulator Name"] || "?"} →{" "}
                  {bellInteraction["Regulated Name"] || "?"}
                  {bellResultsMap[bellRowIndex]
                    ? ` · ${bellResultsMap[bellRowIndex].confidence}`
                    : ""}
                </span>
                <button
                  className="bell-minimized-pill-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    setBellInteraction(null);
                    setBellRowIndex(null);
                    setBellMinimized(false);
                  }}
                  title="Close BELL"
                >
                  ×
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Method change modal */}
      {methodChangeWarning && (
        <div className="method-change-modal-overlay">
          <div className="method-change-modal">
            <div className="method-change-modal-header">
              <span className="method-change-modal-icon">⚠️</span>
              <span className="method-change-modal-title">
                Change Extraction Method?
              </span>
            </div>
            <div className="method-change-modal-body">
              Switching to{" "}
              <strong>
                {methodChangeWarning === "llm"
                  ? "LLM Only"
                  : methodChangeWarning === "indra"
                    ? "NLP Only"
                    : "NLP + LLM"}
              </strong>{" "}
              will permanently clear your current extraction results
              <span className="method-change-modal-count">
                {interactions.length} interaction
                {interactions.length !== 1 ? "s" : ""}
              </span>
              . Download them first if needed.
            </div>
            <div className="method-change-modal-footer">
              <button
                className="modal-btn modal-btn-cancel"
                onClick={cancelMethodChange}
              >
                Keep Current
              </button>
              <button
                className="modal-btn modal-btn-confirm"
                onClick={confirmMethodChange}
              >
                Switch &amp; Clear Data
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KalimbaApp;